import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, OnInit, viewChild, inject, signal, effect, untracked } from '@angular/core';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { forkJoin, lastValueFrom, Observable, of, retry, tap } from 'rxjs';
import { EditorComponent as MonacoEditorComponent } from 'ngx-monaco-editor-v2';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { environment } from '../../../environments/environment';
import { ProjectFormComponent } from '../../shared/components/project-form/project-form';
import { ChapterFormComponent } from '../../shared/components/chapter-form/chapter-form';
import { SceneFormComponent } from '../../shared/components/scene-form/scene-form';
import type { VariantData } from '../../shared/components/variant-editor/variant-editor';
import { ImageFormComponent } from '../../shared/components/image-form/image-form';
import { ConfigPanelComponent } from '../../shared/components/config-panel/config-panel';
import { TimelinePanelComponent } from '../../shared/components/timeline-panel/timeline-panel';
import { GraphPanelComponent } from '../../shared/components/graph-panel/graph-panel';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb';
import { PlayerPanelComponent } from './components/player-panel/player-panel';
import { Select } from 'primeng/select';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { createEditorOptions } from './config/editor-options';
import { EditorConfigService } from './services/editor-config.service';
import { EditorDataService } from './services/editor-data.service';
import { ThemeService } from '../../core/services/theme.service';
import { ProjectsService } from '../../core/services/projects.service';
import { ChapterData, ProjectData } from '../../shared/models';
import { deriveNamesMapKey, deriveSlug, generateEntityId, makeUniqueSlug } from '../../shared/utils/slug';
import { cloneDeep } from '../../shared/utils/clone';
import { attributesMatch } from '../../shared/utils/attributes-match';
import { setRefVariant, type VariantRefLike } from '../../shared/utils/variant-fallback';
import type { DimensionsPatch, ImageRefDimensions } from '../../shared/utils/image-ref-dimensions';
import { mediaHasGeneratedVersions, shouldStampVideoNp } from '../../shared/utils/media-readiness';
import { EditorFilesService } from './services/editor-files.service';
import { EditorJobsService, type JobListItem } from './services/editor-jobs.service';
import { EditorMediaProbeService } from './services/editor-media-probe.service';
import { FileBrowserComponent } from '../../shared/components/file-browser/file-browser';
import { JobsDialogComponent } from '../../shared/components/jobs-dialog/jobs-dialog';
import { SaveHistoryDialogComponent } from '../../shared/components/save-history-dialog/save-history-dialog';
import { ReadonlyBannerComponent } from '../../shared/components/readonly-banner/readonly-banner';
import { CopyTargetDialogComponent, type CopyTargetKind, type CopyTargetSelection } from '../../shared/components/copy-target-dialog/copy-target-dialog';
import { PersonalizedLinkDialogComponent } from '../../shared/components/personalized-link-dialog/personalized-link-dialog';
import { PublishModeDialogComponent, type PublishRequest } from '../../shared/components/publish-mode-dialog/publish-mode-dialog';
import { EditorSlugsService } from './services/editor-slugs.service';
import { applyChapterEdit, chapterEditRejectionMessage, type ChapterEditRejection } from './utils/chapter-edit';
import { isProjectFilePath } from './utils/editor-file-paths';
import { EditorStateService } from './services/editor-state.service';
import { EditLockService } from './services/edit-lock.service';
import { EditorLayoutService } from './services/editor-layout.service';
import { SaveErrorService } from './services/save-error.service';
import { SaveErrorOverlayComponent } from './components/save-error-overlay/save-error-overlay';
import type { ContentItem, ImageRef, JsonData, ProjectFile, SceneAction, SceneData, SceneRef, ImageData } from '../../shared/models';
import {
	cloneActionForCopy,
	cloneImageDocForCopy,
	cloneImageRefForCopy,
	cloneSceneDocForCopy,
	cloneSceneRefForCopy,
	isChapterRelativeSeek,
	takenActionSlugs,
	uniqueSceneSlug,
} from './utils/copy-entity';
import { imagesOf, scenesOf, splitSlugsFor } from './utils/taken-slugs';
import { chaptersToMount, firstAvailableChapterIndex } from './utils/chapters-to-mount';
import { CopyChapterDialogComponent, type CopyChapterSelection } from '../../shared/components/copy-chapter-dialog/copy-chapter-dialog';
import { ImagePreviewDialogComponent, type ImagePreviewRequest } from './components/image-preview-dialog/image-preview-dialog';

interface PendingEntry {
	json: string;
	data: ProjectData | Record<string, unknown> | undefined;
}

@Component({
	selector: 'app-editor',
	imports: [MonacoEditorComponent, FormsModule, ProjectFormComponent, ChapterFormComponent, SceneFormComponent, ImageFormComponent, ConfigPanelComponent, TimelinePanelComponent, GraphPanelComponent, BreadcrumbComponent, PlayerPanelComponent, FileBrowserComponent, JobsDialogComponent, SaveHistoryDialogComponent, ReadonlyBannerComponent, CopyTargetDialogComponent, CopyChapterDialogComponent, PersonalizedLinkDialogComponent, PublishModeDialogComponent, ImagePreviewDialogComponent, SaveErrorOverlayComponent, Select, Menu, Toast, CdkScrollable],
	providers: [EditorConfigService, EditorDataService, EditorFilesService, EditorJobsService, EditorMediaProbeService, EditorSlugsService, EditorStateService, EditLockService, EditorLayoutService, SaveErrorService, MessageService],
	templateUrl: './editor.html',
	styleUrl: './editor.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'(document:mousemove)': 'layoutService.onMouseMove($event)',
		'(document:mouseup)': 'layoutService.onMouseUp()',
		'(document:keydown)': 'onKeyDown($event)',
		'(window:beforeunload)': 'onBeforeUnload($event)',
	},
})
export class EditorComponent implements OnInit {
	// ── View children ────────────────────────────────────────────────────────
	playerPanel = viewChild(PlayerPanelComponent);
	formScroller = viewChild<ElementRef<HTMLDivElement>>('formScroller');

	// ── Fields: protected ───────────────────────────────────────────────────
	protected configService = inject(EditorConfigService);
	protected filesService = inject(EditorFilesService);
	protected jobsService = inject(EditorJobsService);
	private destroyRef = inject(DestroyRef);
	protected projectId = '';
	protected slug = '';
	protected publishUrl = signal<string | null>(null);

	// ── Publish to S3 (DEV-137) ─────────────────────────────────────────────
	protected publishState = signal<'never' | 'pending' | 'running' | 'done' | 'error'>('never');
	protected publishProgress = signal(0);
	protected publishBusy = computed(() => this.publishState() === 'pending' || this.publishState() === 'running');
	private publishPollTimer: ReturnType<typeof setInterval> | null = null;
	/** Listed in the offline showcase app. */
	protected offline = signal(false);
	private offlineSaving = signal(false);

	// ── Download / export ────────────────────────────────────────────────────
	protected exportBusy = signal(false);
	private exportLoadedBytes = signal(0);
	/** From `X-Export-Total-Bytes`; `null` while unknown, which shows MB instead of a percentage. */
	private exportTotalBytes = signal<number | null>(null);
	protected exportProgressLabel = computed(() => {
		const total = this.exportTotalBytes();
		const loaded = this.exportLoadedBytes();
		// Capped: the estimate ignores zip headers, so the real stream runs a little past it.
		if (total) return `${Math.min(99, Math.floor((loaded / total) * 100))}%`;
		return `${(loaded / 1024 / 1024).toFixed(0)} MB`;
	});

	protected get exportUrl(): string {
		return `${environment.apiUrl}/api/projects/${this.slug}/export`;
	}
	protected get exportWithMediaUrl(): string {
		return `${environment.apiUrl}/api/projects/${this.slug}/export-with-media`;
	}
	/**
	 * Editor-local proxies over the shared read-only state. The state service
	 * is the single source of truth — forms, timeline, file-browser, and the
	 * read-only banner all read from `EditorStateService.isReadOnly()`.
	 */
	protected projectSourceUrl = computed(() => this.stateService.projectSourceUrl() ?? null);
	protected isImportedProject = computed(() => this.stateService.isReadOnly());
	/** Toolbar's "⋮ More" dropdown: Download / S3 / Embed groups (PrimeNG Menu's
	 *  native `items` nesting — a subtler group header than a `separator: true`
	 *  divider line). */
	protected moreMenuItems = computed<MenuItem[]>(() => {
		const exporting = this.exportBusy();
		const downloadItems: MenuItem[] = [
			{ label: 'JSON', icon: 'pi pi-file', disabled: exporting, command: (): void => this.exportJson() },
		];
		// Export-with-media bundles locally-uploaded files — imported projects have
		// none, and the backend rejects the call with 403. Hide the menu item for
		// imports so the user never hits that path.
		if (!this.isImportedProject()) {
			downloadItems.push({
				label: exporting ? `Exporting… ${this.exportProgressLabel()}` : 'With media',
				icon: exporting ? 'pi pi-spin pi-spinner' : 'pi pi-folder',
				disabled: exporting,
				command: (): void => this.exportWithMedia(),
			});
		}
		const groups: MenuItem[] = [{ label: 'Download', items: downloadItems }];

		const publishUrl = this.publishUrl();
		if (publishUrl) {
			const s3Items: MenuItem[] = [
				{
					label: this.publishBusy() ? `Publishing… ${this.publishProgress()}%` : 'Publish',
					icon: this.publishBusy() ? 'pi pi-spin pi-spinner' : 'pi pi-cloud-upload',
					disabled: this.publishBusy(),
					command: (): void => this.confirmPublish(),
				},
			];
			if (this.publishState() === 'done') {
				s3Items.push({
					label: 'View',
					icon: 'pi pi-share-alt',
					routerLink: publishUrl,
					target: '_blank',
				});
				// Only offered once a publish has completed (DEV-573) — an
				// unpublished project has no public copy for a personalized link to
				// resolve against, and the create endpoint rejects it with 409.
				s3Items.push({
					label: 'Available offline',
					icon: this.offline() ? 'pi pi-check-square' : 'pi pi-stop',
					disabled: this.offlineSaving(),
					command: (): void => this.toggleOffline(),
				});
				s3Items.push({
					label: 'Personalized link',
					icon: 'pi pi-link',
					command: (): void => this.personalizedLinkDialogVisible.set(true),
				});
			}
			groups.push({ label: 'Publish (S3)', items: s3Items });

			groups.push({ label: 'Embed', items: [{ label: 'View', icon: 'pi pi-share-alt', routerLink: publishUrl }] });
		}

		return groups;
	});
	protected selectedPath = signal<string | null>(null);
	protected fileLoading = signal(false);
	protected editorContent = signal('');
	protected editorDirty = signal(false);
	protected editorSaving = signal(false);
	protected editorSaveError = signal<string | null>(null);
	/** Guards against a second batch save starting while one is still in flight
	 * (the auto-save effect can re-fire while requests are slow/hung), which
	 * would double the concurrent request count and worsen connection pressure. */
	private saveInFlight = false;
	protected pendingChanges = signal<Map<string, PendingEntry>>(new Map());
	protected currentTimeMs = signal(0);
	protected viewMode = signal<'json' | 'form'>('form');
	protected selectedNodeType = signal<'project' | 'chapter' | 'scene' | 'image' | null>(null);
	protected formData = signal<ProjectData | Record<string, unknown> | undefined>(undefined);
	protected projectData = signal<ProjectData | undefined>(undefined);
	protected bottomPanelTab = signal<'timeline' | 'graph'>('timeline');
	protected configPanelOpen = signal(false);
	protected jobsDialogVisible = signal(false);
	protected saveHistoryVisible = signal(false);
	protected copyDialogVisible = signal(false);
	protected personalizedLinkDialogVisible = signal(false);
	protected publishModeDialogVisible = signal(false);
	protected imagePreviewVisible = signal(false);
	/** DEV-655: snapshot of what to render, rebuilt on every click. */
	protected imagePreviewRequest = signal<ImagePreviewRequest | null>(null);
	protected copySourceItem = signal<ContentItem | null>(null);
	protected copySourceLabel = computed(() => {
		const item = this.copySourceItem();
		if (!item) return '';
		const dataType = item.source?.dataType ?? 'ITEM';
		const name = item.name?.trim() || '(unnamed)';
		const slug = item.slug?.trim() || '(no slug)';
		return `${name} · ${slug} · ${dataType}`;
	});
	protected copyActionDialogVisible = signal(false);
	protected copyActionSource = signal<SceneAction | null>(null);
	protected copyActionSourceLabel = computed(() => {
		const action = this.copyActionSource();
		if (!action) return '';
		const type = typeof action.type === 'string' ? action.type : 'ACTION';
		return `${type} · ${action.name?.trim() || action.slug?.trim() || '(unnamed)'}`;
	});
	protected readonly sceneOnlyTargetKinds: CopyTargetKind[] = ['scene'];
	protected copyChapterDialogVisible = signal(false);
	protected copyEntitySource = signal<{ kind: 'scene' | 'image'; chapterSlug: string | null; slug: string } | null>(null);
	protected copyEntitySourceLabel = computed(() => this.copyEntitySource()?.slug ?? '');
	protected copyChapterDialogHeader = computed(() =>
		this.copyEntitySource()?.kind === 'image' ? 'Copy image' : 'Copy scene',
	);
	protected selectedChapterIndex = signal(0);
	protected chapters = computed(() => {
		const data = this.projectData();
		return data?.chapters ?? [];
	});
	/**
	 * DEV-833: a chapter can be gated on arguments too — the player's
	 * `getAvailableChapter()` reads `group`/`groups` + `attributes` off the
	 * chapter ref and skips it when they do not match, exactly as it does for
	 * scenes. The dropdown therefore says which chapters the current argument
	 * values would skip.
	 *
	 * **Marked, not hidden.** Hiding would make a chapter unreachable for
	 * editing precisely when its condition is false — which is when someone
	 * needs to open it.
	 */
	protected chapterOptions = computed(() => {
		const args = this.currentArgsMap();
		return this.chapters().map((ch, i) => {
			const attributes = (ch as unknown as Record<string, unknown>)['attributes'] as
				Record<string, unknown> | undefined;
			const skipped = !!attributes && !attributesMatch(attributes, args);
			return { label: skipped ? `${ch.slug} · skipped` : ch.slug, value: i };
		});
	});
	// The preview player's aspect ratio must follow the currently mounted
	// chapter's own origCoordsWidth/Height when it overrides the project
	// default (e.g. a vertical "reels" chapter inside an otherwise 16:9
	// project) — falling back to the project's value otherwise. Without
	// this, player-panel's CSS aspect-ratio box stays locked to the
	// project's default shape regardless of which chapter is showing, so a
	// correctly-rendered non-standard-dimension canvas gets visually
	// stretched/squished to fit the wrong-shaped box (DEV-402).
	protected previewOrigCoordsWidth = computed(() =>
		this.chapters()[this.selectedChapterIndex()]?.origCoordsWidth ?? this.projectData()?.origCoordsWidth ?? 1920
	);
	protected previewOrigCoordsHeight = computed(() =>
		this.chapters()[this.selectedChapterIndex()]?.origCoordsHeight ?? this.projectData()?.origCoordsHeight ?? 1080
	);
	protected aeComp = computed(() => ({
		fps: this.projectData()?.fps ?? 25,
		width: this.previewOrigCoordsWidth(),
		height: this.previewOrigCoordsHeight(),
	}));
	protected projectNamesMap = computed(() => {
		const nm = (this.projectData() as Record<string, unknown> | undefined)?.['namesMap'];
		return (nm as Record<string, string[]> | undefined) ?? {};
	});
	protected projectNamesMapKeys = computed(() => Object.keys(this.projectNamesMap()));
	protected projectArgKeys = computed(() =>
		this.configService.argDefs().map(d => d.key)
	);
	// ── Fields: private ─────────────────────────────────────────────────────
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private dataService = inject(EditorDataService);
	private themeService = inject(ThemeService);
	private projectsService = inject(ProjectsService);
	private http = inject(HttpClient);
	private messageService = inject(MessageService);
	protected stateService = inject(EditorStateService);
	private slugsService = inject(EditorSlugsService);
	protected editLock = inject(EditLockService);
	protected layoutService = inject(EditorLayoutService);
	protected saveErrorService = inject(SaveErrorService);

	protected editorOptions = computed(() => createEditorOptions(this.themeService.isDark(), this.isImportedProject()));
	private loadedContent = '';
	private originalProjectJson = '';
	/** Last chapter-edit refusal already shown, so a keystroke debounce cannot stack toasts. */
	private chapterEditRejection: ChapterEditRejection | null = null;
	private autoSaveTimer: ReturnType<typeof setTimeout> | null = null;
	private varSaveTimer: ReturnType<typeof setTimeout> | null = null;
	private jsonAutoSaveTimer: ReturnType<typeof setTimeout> | null = null;
	private jsonSyncTimer: ReturnType<typeof setTimeout> | null = null;
	private routeParams = toSignal(this.route.paramMap);
	/** Last `audio` matrix param already resolved — guards the route-params
	 * effect below against re-expanding on every unrelated `projectData()`
	 * change (e.g. an auto-save round-trip), not just on an actual URL change.
	 * Also doubles as "currently selected audio track" for the timeline's
	 * active-block highlight (mirrors `activeSceneSlug` below). */
	protected activeAudioSlug = signal<string | null>(null);
	// ── Computed signals ────────────────────────────────────────────────────
	protected currentArgsMap = computed(() => this.configService.buildArgsMap());
	protected pendingCount = computed(() => this.pendingChanges().size);
	protected globalDirty = computed(() => this.pendingChanges().size > 0);
	protected activeSceneSlug = computed(() => {
		const path = this.selectedPath();
		if (!path) return null;
		const match = path.match(/\/scenes\/([^/]+)\//);
		return match ? match[1] : null;
	});

	/**
	 * PRO-210: the scene's `SceneRef` (as opposed to its `scene.json` content)
	 * lives inside project.json — `chapters[].scenes[]` or top-level `scenes[]`.
	 * Split scenes (`slug__0`, `slug__1`, …) share one ref, so strip the suffix
	 * before matching. Lets `scene-form` show/edit `attributes`/`variant` even
	 * though it's really editing the currently-open scene's chapter/project doc.
	 */
	private currentSceneBaseSlug = computed(() => {
		const slug = this.activeSceneSlug();
		return slug ? slug.replace(/__\d+$/, '') : null;
	});
	private currentSceneRef = computed<Record<string, unknown> | null>(() => {
		if (this.selectedNodeType() !== 'scene') return null;
		const project = this.projectData();
		const baseSlug = this.currentSceneBaseSlug();
		if (!project || !baseSlug) return null;
		const chapterSlug = this.getActiveChapterSlug();
		const list = (chapterSlug
			? project.chapters?.find(c => c.slug === chapterSlug)?.scenes
			: project.scenes) as unknown as Record<string, unknown>[] | undefined;
		return list?.find(s => s['slug'] === baseSlug) ?? null;
	});
	protected currentSceneRefAttributes = computed<Record<string, unknown> | null>(() =>
		(this.currentSceneRef()?.['attributes'] as Record<string, unknown> | undefined) ?? null
	);
	protected currentSceneRefVariant = computed<VariantData | null>(() =>
		(this.currentSceneRef()?.['variant'] as VariantData | undefined) ?? null
	);
	protected currentSceneRefAlt = computed(() => this.currentSceneRef()?.['alt'] === true);
	/**
	 * DEV-697 Phase 2: the open image's `ImageRef` in project.json — the ONLY
	 * place the player reads an image's render size from (`prepareImageProject()`
	 * does `project.dimensions = imageRef.dimensions`; `image.json` has no
	 * dimension field). Same shape as `currentSceneRef` one level up.
	 */
	private currentImageSlug = computed<string | null>(() => {
		const path = this.selectedPath();
		const match = path?.match(/\/images\/([^/]+)\//);
		return match ? match[1] : null;
	});
	private currentImageRefList = (project: ProjectData | null | undefined, chapterSlug: string | null) =>
		(chapterSlug
			? project?.chapters?.find(c => c.slug === chapterSlug)?.images
			: project?.images) as unknown as Record<string, unknown>[] | undefined;
	protected currentImageRef = computed<ImageRefDimensions | null>(() => {
		if (this.selectedNodeType() !== 'image') return null;
		const slug = this.currentImageSlug();
		if (!slug) return null;
		const list = this.currentImageRefList(this.projectData(), this.getActiveChapterSlug());
		return (list?.find(i => i['slug'] === slug) as ImageRefDimensions | undefined) ?? null;
	});
	/**
	 * Widths the project declares, so the Dimensions field can say when a typed
	 * width has no generated file behind it. Advisory only — this is a list of
	 * what exists, NOT a copy of the backend's derivation rule, which stays in
	 * `image-ref-dimensions.service.ts` alone so the two cannot drift.
	 */
	protected projectAvailableWidths = computed<number[]>(() => {
		const project = this.projectData();
		if (!project) return [];
		const maps = [
			project.dimensionsMap,
			...(project.chapters ?? []).map(c => (c as { dimensionsMap?: unknown }).dimensionsMap),
		];
		const widths = new Set<number>();
		for (const map of maps) {
			if (!map || typeof map !== 'object') continue;
			for (const entry of Object.values(map as Record<string, unknown>)) {
				const width = (entry as { width?: unknown } | null)?.width;
				if (typeof width === 'number' && Number.isFinite(width)) widths.add(width);
			}
		}
		return [...widths].sort((a, b) => a - b);
	});

	protected currentSceneSiblingSlugs = computed<string[]>(() => {
		if (this.selectedNodeType() !== 'scene') return [];
		const project = this.projectData();
		const chapterSlug = this.getActiveChapterSlug();
		const list = (chapterSlug
			? project?.chapters?.find(c => c.slug === chapterSlug)?.scenes
			: project?.scenes) as { slug: string }[] | undefined;
		return (list ?? []).map(s => s.slug);
	});

	// ── Constructor (effects) ───────────────────────────────────────────────
	constructor() {
		effect(() => {
			const dark = this.themeService.isDark();
			(window as unknown as { monaco?: { editor?: { setTheme(t: string): void } } }).monaco?.editor?.setTheme(dark ? 'vs-dark' : 'vs');
		});

		// Chapters/args/region → debounce remount + save together (600ms, per
		// spec/context/architecture.md's Config Auto-Save diagram). Previously
		// remounted the player synchronously on every signal change — harmless
		// for a chapters multi-select or an args dropdown, but an args value
		// with no `options` renders as a free-text <input> (config-panel.html),
		// so typing there tore down and recreated the whole <webout-player>
		// element on every keystroke.
		effect(() => {
			this.configService.chapters();
			this.configService.args();
			this.configService.region();
			if (!this.configService.isInitialized) return;
			if (this.autoSaveTimer !== null) clearTimeout(this.autoSaveTimer);
			this.autoSaveTimer = setTimeout(() => {
				this.autoSaveTimer = null;
				untracked(() => {
					this.remountPlayer();
					this.saveConfig();
				});
			}, 600);
		});

		// Vars → setVars immediately, debounced save
		effect(() => {
			this.configService.vars();
			if (!this.configService.isInitialized) return;
			untracked(() => this.playerPanel()?.setVars(this.configService.buildVarsMap()));
			if (this.varSaveTimer !== null) clearTimeout(this.varSaveTimer);
			this.varSaveTimer = setTimeout(() => {
				this.varSaveTimer = null;
				untracked(() => this.saveConfig());
			}, 1000);
		});

		// Auto-save pending JSON changes after 600ms of inactivity
		effect(() => {
			const pending = this.pendingChanges();
			if (this.jsonAutoSaveTimer !== null) {
				clearTimeout(this.jsonAutoSaveTimer);
				this.jsonAutoSaveTimer = null;
			}
			if (!this.stateService.autosaveEnabled()) return;
			if (pending.size === 0) return;
			const hasValid = [...pending.values()].some(e => e.data !== null);
			if (!hasValid) return;
			this.jsonAutoSaveTimer = setTimeout(() => {
				this.jsonAutoSaveTimer = null;
				this.saveFile();
			}, 600);
		});

		// Mount player once view + config are ready
		effect(() => {
			const panel = this.playerPanel();
			const ready = this.configService.ready();
			if (panel && ready) {
				untracked(() => this.remountPlayer());
			}
		});

		// DEV-809: when the codec (aac) generation of a just-dropped audio finishes,
		// its job leaves the active set — re-fire the mount that `remountPlayer()`
		// deferred, so the audio finally comes into the preview. Depends on the
		// active-job signal (and projectData, in case tracks changed meanwhile).
		effect(() => {
			this.jobsService.activeByMediaId();
			this.projectData();
			if (!this.deferredAudioRemount) return;
			untracked(() => this.remountPlayer());
		});

		// Backend rewrites scene/image src refs after image gen flips media.baseUrl
		// to .jpg. Mirror that into the open form so a subsequent save doesn't
		// push the stale URL back.
		effect(() => {
			const rewrite = this.stateService.lastUrlRewrite();
			if (!rewrite) return;
			untracked(() => this.applyUrlRewriteToForm(rewrite.oldUrl, rewrite.newUrl));
		});

		// Service strips `playbackType: VIDEO_NP` from cached scenes once their
		// bg media has playable versions. Mirror into the open form so the user
		// sees the flip immediately, and flush every other affected scene from
		// the service's dirtyPaths into pendingChanges — otherwise the cleared
		// value lives only in memory and is lost on reload (unlike URL rewrites,
		// the backend can't re-derive playbackType for us).
		effect(() => {
			const cleared = this.stateService.lastPlaybackTypeClear();
			if (!cleared) return;
			untracked(() => {
				this.applyPlaybackTypeClearToForm(cleared.mediaUrl);
				this.flushStateServiceDirtyToPending();
			});
		});

		// Single effect that handles both initial selection and back/forward navigation.
		effect(() => {
			const project = this.projectData();
			const params = this.routeParams();
			if (!project || !params) return;

			const chapter = params.get('chapter');
			const scene = params.get('scene');
			const image = params.get('image');
			const audio = params.get('audio');

			if (chapter && !scene && !image) {
				const currentPath = this.selectedPath();
				if (currentPath !== `chapter:${chapter}`) this.selectChapter(chapter);
				this.resolveAudioParam(audio);
				return;
			}

			const targetPath = this.buildFetchPathFromParams(chapter, scene, image);
			if (targetPath !== null && targetPath === this.selectedPath()) {
				this.resolveAudioParam(audio);
				return;
			}

			if (!targetPath) {
				const projectPath = `/projects/${this.slug}/project.json`;
				if (projectPath !== this.selectedPath()) {
					this.selectFile(projectPath, false);
				}
				this.resolveAudioParam(audio);
				return;
			}

			if (targetPath !== this.selectedPath()) {
				this.selectFile(targetPath, false);
			}
			this.resolveAudioParam(audio);
		});
	}

	/**
	 * Resolves the `;audio=` matrix param into an expand request, once per
	 * distinct value — `projectData()`/`routeParams()` can both re-fire this
	 * effect for unrelated reasons (e.g. auto-save), and re-running
	 * `requestAudioExpand` on every tick would re-scroll/re-open a row the
	 * user already collapsed or navigated away from manually.
	 */
	private resolveAudioParam(audio: string | null): void {
		if (!audio) {
			this.activeAudioSlug.set(null);
			return;
		}
		if (audio === this.activeAudioSlug()) return;
		this.activeAudioSlug.set(audio);
		this.viewMode.set('form');
		this.stateService.requestAudioExpand(audio);
	}

	// ── Lifecycle ───────────────────────────────────────────────────────────
	ngOnInit(): void {
		this.projectId = this.route.snapshot.paramMap.get('id') ?? '';
		// Resolve slug from project ID — needed for player paths (/projects/:slug/...)
		this.dataService.loadFile<{ slug: string; companySlug?: string | null; campaignSlug?: string | null }>(`/api/projects/${this.projectId}`).subscribe({
			next: (project) => {
				this.slug = project.slug;
				if (project.companySlug && project.campaignSlug) {
					this.publishUrl.set(`/${project.companySlug}/campaigns/${project.campaignSlug}/${this.projectId}/publisher`);
				}
				// Claim the edit lock now the slug is known. The endpoint no-ops for
				// imported projects (already read-only); a second editor gets a
				// read-only view via EditorStateService.isReadOnly().
				this.editLock.acquire(this.slug);
				this.loadProjectData();
				this.configService.load(this.slug);
				this.loadPublishStatus();
			},
		});
		this.destroyRef.onDestroy(() => {
			this.stopPublishPolling();
			// Release the lock on SPA navigation away from the editor. Full page
			// unload is handled by the beacon in onBeforeUnload.
			this.editLock.release();
		});
	}

	// ── Event handlers (via host) ────────────────────────────────────────────
	onKeyDown(event: KeyboardEvent): void {
		if ((event.metaKey || event.ctrlKey) && event.key === 's') {
			event.preventDefault();
			this.saveFile();
			return;
		}

		if (event.code === 'Space' && !event.repeat && !event.defaultPrevented && !this.isTypingTarget(event.target)) {
			event.preventDefault();
			this.playerPanel()?.togglePlayback();
		}
	}

	/** True when Space should type/act on the focused element instead of toggling playback. */
	private isTypingTarget(target: EventTarget | null): boolean {
		if (!(target instanceof HTMLElement)) return false;
		if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName)) return true;
		if (target.isContentEditable) return true;
		return !!target.closest('.monaco-editor, [role="button"]');
	}

	onBeforeUnload(event: BeforeUnloadEvent): void {
		// Best-effort lock release on tab close / reload; the ~2-min server TTL is
		// the guarantee if the keepalive request doesn't land.
		this.editLock.releaseBeacon();
		if (this.pendingChanges().size > 0 || this.editorDirty()) {
			event.preventDefault();
		}
	}

	/** Used by canDeactivate guard */
	hasUnsavedChanges(): boolean {
		return this.pendingChanges().size > 0 || this.editorDirty();
	}

	// ── Methods: protected ──────────────────────────────────────────────────
	protected exportJson(): void {
		void this.downloadBlob(this.exportUrl, `${this.slug}.zip`);
	}

	protected exportWithMedia(): void {
		void this.downloadBlob(this.exportWithMediaUrl, `${this.slug}.zip`);
	}

	private async downloadBlob(url: string, fallbackName: string): Promise<void> {
		if (this.exportBusy()) return;
		this.exportBusy.set(true);
		this.exportLoadedBytes.set(0);
		this.exportTotalBytes.set(null);
		this.messageService.add({ severity: 'info', summary: 'Export started', detail: 'The download begins once the archive is complete.', life: 4000 });
		try {
			let filename = fallbackName;
			const last = await lastValueFrom(
				this.http.get(url, { responseType: 'blob', observe: 'events', reportProgress: true }).pipe(
					tap((event: HttpEvent<Blob>) => {
						if (event.type === HttpEventType.ResponseHeader) {
							const total = Number(event.headers.get('X-Export-Total-Bytes') ?? event.headers.get('Content-Length'));
							this.exportTotalBytes.set(total > 0 ? total : null);
							filename = this.parseContentDispositionFilename(event.headers.get('Content-Disposition')) ?? fallbackName;
						} else if (event.type === HttpEventType.DownloadProgress) {
							this.exportLoadedBytes.set(event.loaded);
						}
					}),
				),
			);
			const blob = last.type === HttpEventType.Response ? last.body : null;
			if (!blob) throw new Error('Empty response body');
			const objectUrl = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = objectUrl;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			a.remove();
			URL.revokeObjectURL(objectUrl);
			this.messageService.add({
				severity: 'success',
				summary: 'Export ready',
				detail: `${filename} (${(blob.size / 1024 / 1024).toFixed(1)} MB) downloaded.`,
				life: 6000,
			});
		} catch (err: unknown) {
			const detail = err instanceof HttpErrorResponse
				? `${err.status} ${err.statusText || err.message}`
				: err instanceof Error ? err.message : 'Download failed';
			this.messageService.add({ severity: 'error', summary: 'Download failed', detail, life: 8000 });
		} finally {
			this.exportBusy.set(false);
		}
	}

	private parseContentDispositionFilename(header: string | null): string | null {
		if (!header) return null;
		const starMatch = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
		if (starMatch) {
			try { return decodeURIComponent(starMatch[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ }
		}
		const match = /filename=\s*"?([^";]+)"?/i.exec(header);
		return match ? match[1].trim() : null;
	}

	protected onEditorChange(value: string): void {
		this.editorContent.set(value);
		const dirty = value !== this.loadedContent;
		this.editorDirty.set(dirty);
		// Every node type except `chapter` is a file, and `selectedPath()` is that
		// file. A chapter is not: it is a slice of project.json addressed by the
		// synthetic key `chapter:<slug>`, so it is queued further down — against
		// `/projects/<slug>/project.json`, and only once the buffer parses.
		// Queuing the synthetic key here is what dropped every manual chapter
		// edit on the floor, and could fail the whole batch it sat in (DEV-657).
		if (this.selectedNodeType() !== 'chapter') this.trackPending(this.selectedPath(), dirty, value);
		if (this.jsonSyncTimer) clearTimeout(this.jsonSyncTimer);
		if (!dirty) return;
		this.jsonSyncTimer = setTimeout(() => this.flushJsonSync(), 500);
	}

	/**
	 * Mirror the JSON buffer out to wherever the edit actually belongs:
	 * `formData` plus the state service for a file, and — for a chapter — the
	 * splice back into project.json that queues the save at all.
	 *
	 * Debounced from `onEditorChange`, but also flushed synchronously by the
	 * callers that consume the result (a save, a switch to the form view).
	 * Since a chapter's edit is QUEUED here rather than in `onEditorChange`,
	 * skipping the flush would let a Ctrl+S inside the 500 ms window save
	 * nothing at all. A no-op when there is nothing outstanding.
	 */
	private flushJsonSync(): void {
		if (!this.jsonSyncTimer) return;
		clearTimeout(this.jsonSyncTimer);
		this.jsonSyncTimer = null;
		try {
			const parsed = JSON.parse(this.editorContent());
			const path = this.selectedPath();
			const nodeType = this.selectedNodeType();
			if (nodeType === 'chapter') { this.applyChapterJsonEdit(parsed); return; }
			this.formData.set(parsed);
			// Sync to state service so timeline picks up changes instantly
			if (path && nodeType === 'scene') this.stateService.setScene(path, parsed);
			else if (path && nodeType === 'image') this.stateService.setImage(path, parsed);
			else if (nodeType === 'project') this.stateService.projectData.set(parsed);
		} catch { /* invalid JSON */ }
	}

	/**
	 * A chapter edited in the raw JSON view. Splices it back into project.json
	 * and queues THAT file, exactly as `onChapterFormChange` does for the form —
	 * both go through `applyChapterEdit` so the two cannot drift apart again.
	 */
	private applyChapterJsonEdit(parsed: unknown): void {
		const project = this.projectData();
		const chapterSlug = this.getActiveChapterSlug();
		if (!project || !chapterSlug) return;
		const outcome = applyChapterEdit(project, chapterSlug, parsed);
		if (!outcome.ok) { this.reportChapterEditRejected(outcome.reason); return; }
		this.chapterEditRejection = null;
		this.formData.set(parsed as ChapterData);
		this.commitChapterEdit(outcome.project);
	}

	/**
	 * Publish a chapter edit: the project becomes the new `projectData` and the
	 * pending entry is keyed on project.json, not on the chapter's synthetic
	 * selection path. Dirtiness is measured against the whole project, so
	 * undoing a chapter edit by hand drops the pending entry again.
	 */
	private commitChapterEdit(project: ProjectData): void {
		this.projectData.set(project);
		const projectPath = `/projects/${this.slug}/project.json`;
		const projectJson = JSON.stringify(project, null, 2);
		this.trackPending(projectPath, projectJson !== this.originalProjectJson, projectJson);
	}

	/**
	 * Say why a chapter edit was not applied. Deduplicated by reason because the
	 * JSON caller runs on a 500 ms keystroke debounce — a user holding down a key
	 * inside the `slug` field would otherwise stack a toast per pause.
	 */
	private reportChapterEditRejected(reason: ChapterEditRejection): void {
		if (this.chapterEditRejection === reason) return;
		this.chapterEditRejection = reason;
		this.messageService.add({
			severity: 'warn',
			summary: 'Chapter edit not saved',
			detail: chapterEditRejectionMessage(reason),
			life: 8000,
		});
	}

	protected saveFile(): void {
		if (this.saveInFlight) return;
		// A chapter edit is only queued by the JSON mirror, so an explicit save
		// (Ctrl+S) landing inside its debounce window would otherwise find an
		// empty pending map and silently save nothing.
		this.flushJsonSync();
		const pending = this.pendingChanges();
		if (pending.size === 0) return;

		this.editorSaving.set(true);
		this.editorSaveError.set(null);
		this.saveErrorService.setRetry(() => this.saveFile());

		// Build parsed entries, skipping unparseable ones
		const parsedPending = new Map<string, unknown>();
		for (const [filePath, entry] of pending.entries()) {
			let data: unknown;
			try {
				data = typeof entry.data === 'object' && entry.data !== null ? entry.data : JSON.parse(entry.json);
			} catch {
				continue;
			}
			parsedPending.set(filePath, data);
		}

		if (parsedPending.size === 0) {
			this.editorSaveError.set('Invalid JSON — fix errors before saving');
			this.editorSaving.set(false);
			return;
		}

		this.saveInFlight = true;
		// `saveFilesPartial` never errors: it reports per file so we can drop only
		// the files that actually saved and leave the failures pending — a retry
		// then re-sends just those, instead of re-firing the whole batch (which is
		// what let a single flaky request wedge the save indefinitely).
		this.dataService.saveFilesPartial(parsedPending, 'autosave').subscribe({
			next: (result) => {
				this.saveInFlight = false;
				this.editorSaving.set(false);

				if (result.succeeded.length > 0) {
					this.pendingChanges.update((prev) => {
						const next = new Map(prev);
						for (const path of result.succeeded) next.delete(path);
						return next;
					});
					this.stateService.markClean(result.succeeded);

					const projectPath = `/projects/${this.slug}/project.json`;
					if (result.succeeded.includes(projectPath)) {
						this.originalProjectJson = JSON.stringify(parsedPending.get(projectPath), null, 2);
					}
				}

				if (result.failed.length > 0) {
					// Banner/overlay already raised by SaveErrorService; keep the
					// failed files pending for the next (auto-)save to retry.
					this.editorSaveError.set('Save failed');
					return;
				}

				// Full success.
				this.loadedContent = this.editorContent();
				this.editorDirty.set(false);
				this.refreshImageRefDimensions();
				// Bump timeline version so it re-fetches scene data (picks up framesCount changes)
				this.timelineVersion.update(v => v + 1);
				this.remountPlayer();
			},
			error: (err) => {
				// saveFilesPartial doesn't error, but never let a thrown bug leave
				// saveInFlight stuck true (which would block every future save).
				this.saveInFlight = false;
				this.editorSaving.set(false);
				this.editorSaveError.set(err?.error?.error ?? 'Save failed');
			},
		});
	}

	/**
	 * Enumerates every audio doc in the project missing its mp3/aac codec
	 * sibling and queues a "Generate versions" job for each — same mechanism
	 * autoGenerateForMedia() uses for one doc, just project-wide. The backend
	 * returns immediately once jobs are queued (not once they finish); the
	 * existing jobs-dialog picks them all up via the next loadActiveJobs()
	 * poll, same as any other job.
	 */
	protected generatingMissingAudioCodecs = signal(false);
	protected generateMissingAudioCodecs(): void {
		if (this.generatingMissingAudioCodecs()) return;
		this.generatingMissingAudioCodecs.set(true);
		this.filesService.generateMissingAudioCodecs(this.slug)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ({ queued, total }) => {
					this.generatingMissingAudioCodecs.set(false);
					this.jobsService.loadActiveJobs(this.slug);
					this.messageService.add({
						severity: queued > 0 ? 'success' : 'info',
						summary: 'Audio codec generation',
						detail: queued > 0
							? `Queued ${queued} of ${total} audio file(s) missing a codec.`
							: total > 0
								? `${total} audio file(s) already have jobs running.`
								: 'Every audio file already has both codecs.',
						life: 5000,
					});
				},
				error: (err) => {
					this.generatingMissingAudioCodecs.set(false);
					this.messageService.add({ severity: 'error', summary: 'Audio codec generation failed', detail: err?.error?.error ?? 'Unknown error', life: 6000 });
				},
			});
	}

	/**
	 * Queues an incremental generation job for every video/mask missing a
	 * resolution the project declares (DEV-688). The regression it repairs:
	 * media generated while the platform only produced `medium` play fine on
	 * desktop and 404 on every iOS device, which the player sends to `low`.
	 *
	 * The backend deliberately leaves some incomplete media alone — imported
	 * catalogs, composites, variant groups, images — so the toast reports that
	 * count rather than letting "queued 0" read as "nothing was wrong".
	 */
	protected generatingMissingWidths = signal(false);
	protected generateMissingWidths(): void {
		if (this.generatingMissingWidths()) return;
		this.generatingMissingWidths.set(true);
		this.filesService.generateMissingWidths(this.slug)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (result) => {
					this.generatingMissingWidths.set(false);
					this.jobsService.loadActiveJobs(this.slug);
					const skipped = result.skippedImported + result.skippedComposites
						+ result.skippedVariantGroups + result.skippedImages;
					const tail = skipped > 0 ? ` ${skipped} other incomplete file(s) need a different repair.` : '';
					this.messageService.add({
						severity: result.queued > 0 ? 'success' : 'info',
						summary: 'Resolution generation',
						detail: !result.declaredWidths.length
							? 'This project declares no resolutions — set them on the project before generating.'
							: result.queued > 0
								? `Queued ${result.queued} of ${result.total} file(s) missing a resolution.${tail}`
								: result.total > 0
									? `Nothing queued.${result.alreadyQueued > 0 ? ` ${result.alreadyQueued} file(s) already have jobs running.` : ''}${tail}`
									: `Every file already has all declared resolutions (${result.declaredWidths.join(', ')}).`,
						life: 6000,
					});
				},
				error: (err) => {
					this.generatingMissingWidths.set(false);
					this.messageService.add({ severity: 'error', summary: 'Resolution generation failed', detail: err?.error?.error ?? 'Unknown error', life: 6000 });
				},
			});
	}

	/** Re-queues version generation for any image whose baseUrl doesn't match one of its own generated files — the PRO-198 signature (black player preview from a 404'd background). */
	protected repairingBrokenImageBaseUrls = signal(false);
	protected repairBrokenImageBaseUrls(): void {
		if (this.repairingBrokenImageBaseUrls()) return;
		this.repairingBrokenImageBaseUrls.set(true);
		this.filesService.repairBrokenImageBaseUrls(this.slug)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: ({ queued, total }) => {
					this.repairingBrokenImageBaseUrls.set(false);
					this.jobsService.loadActiveJobs(this.slug);
					this.messageService.add({
						severity: queued > 0 ? 'success' : 'info',
						summary: 'Image baseUrl repair',
						detail: queued > 0
							? `Queued ${queued} of ${total} image(s) with a broken baseUrl.`
							: total > 0
								? `${total} image(s) already have repair jobs running.`
								: 'No broken image references found.',
						life: 5000,
					});
				},
				error: (err) => {
					this.repairingBrokenImageBaseUrls.set(false);
					this.messageService.add({ severity: 'error', summary: 'Image baseUrl repair failed', detail: err?.error?.error ?? 'Unknown error', life: 6000 });
				},
			});
	}

	protected confirmPublish(): void {
		if (this.publishBusy()) return;
		this.publishModeDialogVisible.set(true);
	}

	protected publishToS3(request: PublishRequest): void {
		this.publishState.set('pending');
		this.publishProgress.set(0);
		this.http.post<{ status: string }>(`${environment.apiUrl}/api/projects/${this.slug}/publish`, request)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.publishState.set('running');
					this.startPublishPolling();
				},
				error: (err) => {
					this.publishState.set('error');
					this.messageService.add({ severity: 'error', summary: 'Publish failed', detail: err?.error?.error ?? 'Unknown error', life: 6000 });
				},
			});
	}

	private toggleOffline(): void {
		const offline = !this.offline();
		this.offline.set(offline);
		this.offlineSaving.set(true);
		this.http.put<{ offline: boolean }>(`${environment.apiUrl}/api/projects/${this.slug}/publish/offline`, { offline })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.offlineSaving.set(false);
					this.messageService.add({
						severity: 'success',
						summary: offline ? 'Available offline' : 'Removed from offline',
						detail: offline ? 'The project now shows in the Fourtly Showcase app' : 'The project no longer shows in the Fourtly Showcase app',
						life: 3000,
					});
				},
				error: () => {
					this.offlineSaving.set(false);
					this.offline.set(!offline);
					this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Could not change offline availability', life: 4000 });
				},
			});
	}

	private loadPublishStatus(): void {
		if (!this.slug) return;
		this.http.get<{
			status: 'never' | 'pending' | 'running' | 'done' | 'error';
			progress?: number;
			uploadedCount?: number;
			skippedCount?: number;
			deletedCount?: number;
			failedCount?: number;
			missingLevels?: string[];
			error?: string;
			offline?: boolean;
		}>(`${environment.apiUrl}/api/projects/${this.slug}/publish`)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (res) => {
					const wasRunning = this.publishBusy();
					this.publishState.set(res.status);
					this.publishProgress.set(res.progress ?? 0);
					this.offline.set(res.offline ?? false);
					if (res.status === 'pending' || res.status === 'running') this.startPublishPolling();
					else this.stopPublishPolling();

					// Under diff most files are skipped, so a run that uploaded 3 of
					// 200 files looks broken from the progress badge alone. Report
					// what actually happened once the run we were watching finishes.
					if (wasRunning && res.status === 'done') {
						const parts = [`${res.uploadedCount ?? 0} uploaded`];
						if (res.skippedCount) parts.push(`${res.skippedCount} unchanged`);
						if (res.deletedCount) parts.push(`${res.deletedCount} removed`);
						this.messageService.add({
							severity: 'success',
							summary: 'Published',
							detail: parts.join(', '),
							life: 5000,
						});
					}
					// A successful publish can still be incomplete in a way the
					// counts don't show: media missing a resolution the project
					// declares is absent from the plan, so it is neither
					// uploaded nor failed — it simply 404s for whichever
					// viewport resolves to it (iOS goes to `low`). Warn beside
					// the success toast; "Generate missing resolutions" in the
					// maintenance menu is the repair. DEV-688.
					if (wasRunning && res.status === 'done' && res.missingLevels?.length) {
						this.messageService.add({
							severity: 'warn',
							summary: 'Published with missing resolutions',
							// The list is a capped sample (its own last line says how
							// many were dropped), so don't quote its length as a total.
							detail: `${res.missingLevels[0]}${res.missingLevels.length > 1 ? ', and more' : ''} — run "Generate missing resolutions" to fill them in.`,
							life: 12000,
						});
					}
					// A run that ends in 'error' after uploading most of the
					// project used to leave only a badge behind — the toast is
					// what the user actually reads.
					if (wasRunning && res.status === 'error') {
						this.messageService.add({
							severity: 'error',
							summary: res.failedCount ? 'Published with missing media' : 'Publish failed',
							detail: res.error ?? 'Unknown error',
							life: 10000,
						});
					}
				},
				error: () => this.publishState.set('never'),
			});
	}

	private startPublishPolling(): void {
		if (this.publishPollTimer) return;
		this.publishPollTimer = setInterval(() => this.loadPublishStatus(), 2000);
	}

	private stopPublishPolling(): void {
		if (this.publishPollTimer) {
			clearInterval(this.publishPollTimer);
			this.publishPollTimer = null;
		}
	}

	protected maintenanceMenuItems = computed<MenuItem[]>(() => [
		{
			label: 'Generate missing resolutions',
			icon: 'pi pi-arrows-alt',
			disabled: this.generatingMissingWidths(),
			command: (): void => this.generateMissingWidths(),
		},
		{
			label: 'Generate missing audio codecs',
			icon: 'pi pi-volume-up',
			disabled: this.generatingMissingAudioCodecs(),
			command: (): void => this.generateMissingAudioCodecs(),
		},
		{
			label: 'Repair broken image thumbnails',
			icon: 'pi pi-image',
			disabled: this.repairingBrokenImageBaseUrls(),
			command: (): void => this.repairBrokenImageBaseUrls(),
		},
	]);

	protected switchToForm(): void {
		// The form renders `formData`, so the buffer has to be mirrored out before
		// the switch — for a chapter that mirror is also what queues the save.
		this.flushJsonSync();
		this.viewMode.set('form');
	}

	protected navigateToProject(): void {
		this.selectFile(`/projects/${this.slug}/project.json`);
	}

	protected selectFile(fetchPath: string, updateUrl = true): void {
		this.fileLoading.set(true);
		this.selectedPath.set(fetchPath);
		this.selectedNodeType.set(this.inferNodeType(fetchPath));

		if (updateUrl) {
			this.updateUrl(fetchPath);
		}

		this.dataService.loadFile<Record<string, unknown>>(fetchPath).subscribe({
			next: (data) => {
				const pending = this.pendingChanges().get(fetchPath);
				if (pending) {
					this.editorContent.set(pending.json);
					this.loadedContent = JSON.stringify(data, null, 2);
					this.editorDirty.set(true);
					this.formData.set(pending.data);
				} else {
					this.editorContent.set(JSON.stringify(data, null, 2));
					this.loadedContent = this.editorContent();
					this.editorDirty.set(false);
					this.formData.set(data);
				}
				// Populate state service cache (read-only — does not bump `version`,
				// so merely viewing a scene doesn't make the timeline re-fetch and
				// lose its scroll position).
				const nodeType = this.inferNodeType(fetchPath);
				if (nodeType === 'scene') this.stateService.cacheScene(fetchPath, data as unknown as import('@webout/types').SceneData);
				else if (nodeType === 'image') this.stateService.setImage(fetchPath, data as unknown as import('@webout/types').ImageData);
				this.editorSaveError.set(null);
				this.fileLoading.set(false);
				this.scrollFormToTop();
			},
			error: () => {
				this.editorContent.set('// Failed to load file');
				this.formData.set(undefined);
				this.fileLoading.set(false);
			},
		});
	}

	protected selectChapter(chapterSlug: string): void {
		const project = this.projectData();
		if (!project) return;
		const chapter = (project.chapters ?? []).find(c => c.slug === chapterSlug);
		if (!chapter) return;
		this.selectedNodeType.set('chapter');
		this.selectedPath.set(`chapter:${chapterSlug}`);
		this.chapterEditRejection = null;
		this.formData.set(chapter);
		this.editorContent.set(JSON.stringify(chapter, null, 2));
		this.loadedContent = this.editorContent();
		this.editorDirty.set(false);
		this.fileLoading.set(false);
		this.viewMode.set('form');
		const idx = this.chapters().findIndex(c => c.slug === chapterSlug);
		if (idx >= 0) {
			this.selectedChapterIndex.set(idx);
			// Only remount on explicit user navigation; initial load is handled by the
			// "mount player once ready" effect which reads selectedChapterIndex at that point.
			if (this.configService.isInitialized) {
				this.remountPlayer();
				this.playerPanel()?.resetZoom();
			}
		}
		this.navigateToEditorState({ chapter: chapterSlug });
		this.scrollFormToTop();
	}

	private navigateToEditorState(matrix: Record<string, string>): void {
		const { companySlug, campaignSlug } = this.route.snapshot.params;
		this.router.navigate(['/', companySlug, 'campaigns', campaignSlug, this.projectId, 'editor', matrix]);
	}

	private populateNamesMapFromGroup(project: ProjectData, baseUrl: string): void {
		const media = this.filesService.files().find(f => f.baseUrl === baseUrl);
		if (!media?.variants?.length) return;
		const key = deriveNamesMapKey(media.name);
		if (!key) return;
		const content = project as Record<string, unknown>;
		if (!content['namesMap']) content['namesMap'] = {};
		(content['namesMap'] as Record<string, string[]>)[key] = media.variants.map(v => v.name);
	}

	private autoGenerateForMedia(baseUrl: string, resolved?: ProjectFile): void {
		const media = resolved ?? this.filesService.files().find(f => f.baseUrl === baseUrl);
		const mediaId = media?.id;
		// Must agree with `shouldStampVideoNp` — the flag is only ever cleared by
		// this job's completion event (DEV-530).
		if (media?.isLocal && mediaId && !mediaHasGeneratedVersions(media)) {
			this.filesService.generateVersions(mediaId)
				.pipe(this.jobsService.andTrack({ mediaId, mediaName: media.name ?? '', destroyRef: this.destroyRef }))
				.subscribe();
		}
	}

	/**
	 * Keep an already-established `namesMap` entry in step with the variant group
	 * it describes. The entry is written once, when the group is first wired into
	 * a scene or an audio track, from the names the group had at that moment —
	 * every name added afterwards was invisible to the player, which reads its
	 * list of variants from this map.
	 *
	 * Only refreshes keys that already exist: uploading into a group nothing
	 * references yet shouldn't invent a `namesMap` entry for it.
	 */
	protected onVariantGroupChanged(mediaId: string): void {
		const media = this.filesService.files().find(f => f.id === mediaId);
		if (!media?.variants) return;
		const key = deriveNamesMapKey(media.name ?? '');
		if (!key) return;
		const current = this.projectNamesMap()[key];
		if (!current) return;
		const names = media.variants.map(v => v.name);
		if (current.length === names.length && current.every((n, i) => n === names[i])) return;
		this.onNamesMapPopulate({ key, names });
	}

	/** A media delete cascaded into the timeline server-side (removed referencing
	 * scenes / audio-track entries). Reload the project so those now-gone entries
	 * drop out of the timeline. */
	protected onTimelineChangedExternally(): void {
		this.loadProjectData();
	}

	/**
	 * A media was deleted from the pool; the backend already stripped its src from
	 * every referencing scene (cleanMediaReferences). Surgically drop that one
	 * media's src from the OPEN scene/image form too — clearing just its media
	 * field — instead of re-fetching (and resetting) the whole file. The content
	 * the server now holds matches, so this is the clean saved baseline, not an
	 * edit: mark it loaded and drop any stale pending entry so it isn't re-saved.
	 */
	protected onMediaDeleted(mediaId: string): void {
		if (!mediaId) return;
		const nodeType = this.selectedNodeType();
		if (nodeType !== 'scene' && nodeType !== 'image') return;
		const current = this.formData();
		if (!current) return;
		const cloned = cloneDeep(current);
		if (!this.stripMediaSrcDeep(cloned, `/${mediaId}/`)) return;
		this.formData.set(cloned);
		const json = JSON.stringify(cloned, null, 2);
		this.editorContent.set(json);
		this.loadedContent = json;
		this.editorDirty.set(false);
		this.trackPending(this.selectedPath(), false, json);
	}

	/** Delete every string leaf that points at `needle` (a `/<mediaId>/` path), keeping all config. Returns whether anything changed. */
	private stripMediaSrcDeep(node: unknown, needle: string): boolean {
		let changed = false;
		if (Array.isArray(node)) {
			for (const el of node) if (this.stripMediaSrcDeep(el, needle)) changed = true;
			return changed;
		}
		if (node && typeof node === 'object') {
			for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
				if (typeof value === 'string') {
					if (value.includes(needle)) { delete (node as Record<string, unknown>)[key]; changed = true; }
				} else if (this.stripMediaSrcDeep(value, needle)) {
					changed = true;
				}
			}
		}
		return changed;
	}

	protected onNamesMapPopulate(event: { key: string; names: string[] }): void {
		const current = this.projectData();
		if (!current) return;
		const project = cloneDeep(current);
		const content = project as Record<string, unknown>;
		if (!content['namesMap']) content['namesMap'] = {};
		(content['namesMap'] as Record<string, string[]>)[event.key] = event.names;
		this.projectData.set(project);
		const projectPath = `/projects/${this.slug}/project.json`;
		const projectJson = JSON.stringify(project, null, 2);
		const dirty = projectJson !== this.originalProjectJson;
		this.trackPending(projectPath, dirty, projectJson);
	}

	/** PRO-210: attributes-editor inside `scene-form` edits the open scene's `SceneRef.attributes` in project.json. */
	protected onSceneRefAttributesChange(attrs: Record<string, unknown>): void {
		this.patchCurrentSceneRef(ref => {
			const keys = Object.keys(attrs);
			if (keys.length > 0) {
				ref['attributes'] = attrs;
				ref['groups'] = keys;
			} else {
				delete ref['attributes'];
				delete ref['groups'];
			}
		});
	}

	/** PRO-210: variant-editor inside `scene-form` edits the open scene's `SceneRef.variant` in project.json. */
	protected onSceneRefVariantChange(variant: VariantData | null): void {
		this.patchCurrentSceneRef((ref, siblings) => {
			setRefVariant(siblings as unknown as VariantRefLike[], ref as unknown as VariantRefLike, variant);
		});
	}

	protected onSceneRefAltChange(alt: boolean): void {
		this.patchCurrentSceneRef(ref => {
			if (alt) ref['alt'] = true;
			else delete ref['alt'];
		});
	}

	/**
	 * DEV-697 Phase 2: the Dimensions section in `image-form` edits the open
	 * image's `ImageRef`, not its `image.json`. A `null` field is REMOVED rather
	 * than written as 0 — an absent field is what makes the backend's
	 * `fillImageRefDimensions()` derive one again on the next save, which is how
	 * "reset to the project's size" works without a second copy of the rule here.
	 */
	protected onImageRefDimensionsChange(patch: DimensionsPatch): void {
		// Clearing the override hands the size back to the backend, which
		// re-derives it on save. Until that value comes back the ref genuinely
		// has none — which is honest, but it also disables "Render preview"
		// (the render would hang without a size) until someone reloads. Note
		// the reset so the save can pick the derived value up. DEV-697/655.
		if (!patch.dimensions && patch.origCoordsWidth === null) this.imageRefDimensionsReset = true;
		this.patchCurrentImageRef(ref => {
			if (patch.dimensions) ref['dimensions'] = patch.dimensions;
			else delete ref['dimensions'];
			if (patch.origCoordsWidth !== null) ref['origCoordsWidth'] = patch.origCoordsWidth;
			else delete ref['origCoordsWidth'];
		});
	}

	/**
	 * DEV-655: render the open image on its own canvas and show it.
	 *
	 * A fresh request object per click — the dialog keys its render on identity,
	 * so clicking again after fixing a width re-renders rather than showing the
	 * old result. The args and vars are the same maps the centre preview is
	 * mounted with, so the render is personalized exactly like the live preview.
	 */
	protected onImagePreviewRequest(): void {
		const slug = this.currentImageSlug();
		const path = this.selectedPath();
		if (!slug || !path) return;
		this.imagePreviewRequest.set({
			projectSlug: this.slug,
			signatureUuid: this.projectId,
			imageSlug: slug,
			imageName: (this.formData() as { name?: string } | undefined)?.name ?? slug,
			ref: this.currentImageRef(),
			imagePath: path,
			args: this.configService.buildArgsMap(),
			vars: this.configService.buildVarsMap(),
			// The editor previews the working copy, which only this backend serves.
			jsonApiUrl: environment.backendUrl,
			region: this.configService.region(),
		});
		this.imagePreviewVisible.set(true);
	}

	private patchCurrentImageRef(mutator: (ref: Record<string, unknown>) => void): void {
		const current = this.projectData();
		const slug = this.currentImageSlug();
		if (!current || !slug) return;
		const project = cloneDeep(current);
		const list = this.currentImageRefList(project, this.getActiveChapterSlug());
		const ref = list?.find(i => i['slug'] === slug);
		if (!ref) return;
		mutator(ref);
		this.projectData.set(project);
		const projectPath = `/projects/${this.slug}/project.json`;
		const projectJson = JSON.stringify(project, null, 2);
		const dirty = projectJson !== this.originalProjectJson;
		this.trackPending(projectPath, dirty, projectJson);
	}

	private patchCurrentSceneRef(mutator: (ref: Record<string, unknown>, siblings: Record<string, unknown>[]) => void): void {
		const current = this.projectData();
		const baseSlug = this.currentSceneBaseSlug();
		if (!current || !baseSlug) return;
		const project = cloneDeep(current);
		const chapterSlug = this.getActiveChapterSlug();
		const list = (chapterSlug
			? project.chapters?.find(c => c.slug === chapterSlug)?.scenes
			: project.scenes) as unknown as Record<string, unknown>[] | undefined;
		const ref = list?.find(s => s['slug'] === baseSlug);
		if (!list || !ref) return;
		mutator(ref, list);
		this.projectData.set(project);
		const projectPath = `/projects/${this.slug}/project.json`;
		const projectJson = JSON.stringify(project, null, 2);
		const dirty = projectJson !== this.originalProjectJson;
		this.trackPending(projectPath, dirty, projectJson);
	}

	protected onChapterFormChange(updated: ChapterData): void {
		const current = this.projectData();
		const chapterSlug = this.getActiveChapterSlug();
		if (!current || !chapterSlug) return;
		const outcome = applyChapterEdit(current, chapterSlug, updated);
		if (!outcome.ok) { this.reportChapterEditRejected(outcome.reason); return; }
		this.chapterEditRejection = null;
		this.formData.set(updated);
		// Keep the JSON buffer in step with the form and treat it as saved-clean:
		// the form's edit is queued as project.json below, so the chapter buffer
		// itself has nothing outstanding of its own.
		const json = JSON.stringify(updated, null, 2);
		this.loadedContent = json;
		this.editorContent.set(json);
		this.commitChapterEdit(outcome.project);
	}

	protected onBreadcrumbClick(action: string): void {
		if (action === 'project') {
			this.navigateToProject();
		} else if (action.startsWith('chapter:')) {
			this.selectChapter(action.substring('chapter:'.length));
		}
	}

	protected selectImageFromForm(event: { chapterSlug: string | null; imageSlug: string }): void {
		const path = !event.chapterSlug
			? `/projects/${this.slug}/images/${event.imageSlug}/image.json`
			: `/projects/${this.slug}/chapters/${event.chapterSlug}/images/${event.imageSlug}/image.json`;
		this.selectFile(path);
	}

	protected selectSceneFromForm(event: { chapterSlug: string | null; sceneSlug: string }): void {
		const path = !event.chapterSlug
			? `/projects/${this.slug}/scenes/${event.sceneSlug}/scene.json`
			: `/projects/${this.slug}/chapters/${event.chapterSlug}/scenes/${event.sceneSlug}/scene.json`;
		this.selectFile(path);
	}

	protected selectAudioFromTimeline(event: { chapterSlug: string | null; audioSlug: string }): void {
		// `selectChapter`/`navigateToProject` always re-fetch + reset form state,
		// even when we're already on the right node — clicking a second audio
		// block in the same scope would otherwise re-trigger a full reload and
		// visibly "jump". Only navigate if we're not already there.
		const alreadyOnTarget = event.chapterSlug
			? this.selectedNodeType() === 'chapter' && this.selectedPath() === `chapter:${event.chapterSlug}`
			: this.selectedNodeType() === 'project';
		if (!alreadyOnTarget) {
			if (event.chapterSlug) this.selectChapter(event.chapterSlug);
			else this.navigateToProject();
		}
		// The audio editor only renders in Form view.
		this.viewMode.set('form');
		this.stateService.requestAudioExpand(event.audioSlug);
		// `selectChapter`/`navigateToProject` above already wrote `;chapter=`
		// (or cleared it) via their own `navigateToEditorState` call — this
		// overwrites with the full matrix so `;audio=` survives a refresh too
		// (mirrors scene/image selection, which already round-trips through
		// the URL — see the route-params effect in the constructor).
		this.activeAudioSlug.set(event.audioSlug);
		this.navigateToEditorState(
			event.chapterSlug ? { chapter: event.chapterSlug, audio: event.audioSlug } : { audio: event.audioSlug },
		);
	}

	// ── Copy content item to another scene/image ────────────────────────────
	protected onCopyContentItem(item: JsonData): void {
		// Defence in depth — the copy button is also disabled by the parent
		// fieldset on imported projects, but a refactor that breaks that
		// propagation must NOT silently start writing to a read-only project.
		if (this.stateService.isReadOnly()) return;
		this.copySourceItem.set(item as ContentItem);
		this.copyDialogVisible.set(true);
	}

	protected onCopyTargetSelected(target: CopyTargetSelection): void {
		// The dialog is mounted outside the read-only fieldset, so it needs its
		// own guard rather than relying on the disabled propagation.
		if (this.stateService.isReadOnly()) return;
		const source = this.copySourceItem();
		if (!source) return;
		const targetPath = this.buildTargetPath(target);
		this.loadTargetContent(targetPath, target.kind).subscribe({
			next: (current) => this.applyCopyToTarget(targetPath, target, current, source),
			error: () => {
				this.messageService.add({
					severity: 'error', summary: 'Copy failed',
					detail: `Could not load ${target.kind} "${target.slug}".`,
				});
			},
		});
	}

	private buildTargetPath(target: CopyTargetSelection): string {
		if (target.kind === 'scene') {
			return target.chapterSlug
				? `/projects/${this.slug}/chapters/${target.chapterSlug}/scenes/${target.slug}/scene.json`
				: `/projects/${this.slug}/scenes/${target.slug}/scene.json`;
		}
		return target.chapterSlug
			? `/projects/${this.slug}/chapters/${target.chapterSlug}/images/${target.slug}/image.json`
			: `/projects/${this.slug}/images/${target.slug}/image.json`;
	}

	private loadTargetContent(path: string, kind: 'scene' | 'image'): Observable<SceneData | ImageData> {
		const cached = kind === 'scene' ? this.stateService.peekScene(path) : this.stateService.peekImage(path);
		if (cached) return of(cached);
		return this.dataService.loadFile<SceneData | ImageData>(path);
	}

	private applyCopyToTarget(
		targetPath: string,
		target: CopyTargetSelection,
		current: SceneData | ImageData,
		source: ContentItem,
	): void {
		const cloned = cloneDeep(current);
		const existingContent = (cloned.content ?? []) as ContentItem[];
		const taken = new Set<string>(existingContent.map((c) => c.slug).filter((s): s is string => !!s));
		const baseSlug = source.slug ? deriveSlug(source.slug) : 'item';
		const newItem = cloneDeep(source) as ContentItem;
		newItem.id = generateEntityId();
		newItem.slug = makeUniqueSlug(baseSlug || 'item', taken);
		cloned.content = [...existingContent, newItem];

		if (target.kind === 'scene') {
			this.stateService.setScene(targetPath, cloned as SceneData);
		} else {
			this.stateService.setImage(targetPath, cloned as ImageData);
		}
		this.copySourceItem.set(null);

		// DEV-810: the open form renders `formData`, a parallel copy of the same
		// document — mirror the copy into it, or the new item stays invisible
		// until a re-navigation AND the form's next `patch()` clones the
		// pre-copy snapshot straight back over it.
		const json = JSON.stringify(cloned, null, 2);
		this.syncFormFromDocument(targetPath, cloned);
		this.refreshPendingForPath(targetPath, json);

		// Save immediately and surface success/failure to the user — earlier we
		// fired the success toast synchronously after `trackPending`, before the
		// debounced PUT ran. On read-only projects (sourceUrl !== 'local') the
		// PUT 403s, but the user already saw "copied". Save now and toast off
		// the response instead. Mirrors the pattern at `onTimelineFileDrop`.
		this.dataService.saveFiles(new Map([[targetPath, cloned as unknown]]), 'copy-content-item').subscribe({
			next: () => {
				this.markDocumentSaved(targetPath, json);
				this.messageService.add({
					severity: 'success', summary: 'Content item copied',
					detail: `Copied to ${target.kind} "${target.slug}" as "${newItem.slug}".`,
				});
			},
			error: (err) => {
				this.markDocumentDirty(targetPath, json);
				this.messageService.add({
					severity: 'error', summary: 'Copy failed',
					detail: err?.error?.error ?? `Could not save ${target.kind} "${target.slug}".`,
				});
			},
		});
	}

	// ── Copy action to another scene ────────────────────────────────────────
	protected onCopyAction(action: SceneAction): void {
		if (this.stateService.isReadOnly()) return;
		this.copyActionSource.set(action);
		this.copyActionDialogVisible.set(true);
	}

	protected onCopyActionTargetSelected(target: CopyTargetSelection): void {
		// The dialog is mounted outside the read-only fieldset, so it needs its
		// own guard rather than relying on the disabled propagation.
		if (this.stateService.isReadOnly()) return;
		const source = this.copyActionSource();
		if (!source) return;
		const targetPath = this.buildTargetPath(target);
		this.loadTargetContent(targetPath, target.kind).subscribe({
			next: (current) => this.applyActionCopyToTarget(targetPath, target, current as SceneData, source),
			error: () => {
				this.messageService.add({
					severity: 'error', summary: 'Copy failed',
					detail: `Could not load scene "${target.slug}".`,
				});
			},
		});
	}

	private applyActionCopyToTarget(
		targetPath: string,
		target: CopyTargetSelection,
		current: SceneData,
		source: SceneAction,
	): void {
		const cloned = cloneDeep(current);
		const existing = cloned.actions ?? [];
		const newAction = cloneActionForCopy(source, takenActionSlugs(existing));
		cloned.actions = [...existing, newAction];

		this.stateService.setScene(targetPath, cloned);
		this.copyActionSource.set(null);

		// DEV-810 — see `applyCopyToTarget`: the Actions list goes stale the
		// same way, and the revert-on-next-keystroke is the same too.
		const json = JSON.stringify(cloned, null, 2);
		this.syncFormFromDocument(targetPath, cloned);
		this.refreshPendingForPath(targetPath, json);

		// A SEEK without an explicit chapter means "this scene's chapter", so it
		// resolves elsewhere once copied across chapters. Flagged, not rewritten.
		const seekHint = isChapterRelativeSeek(newAction) ? ' · check its SEEK target (chapter-relative)' : '';

		this.dataService.saveFiles(new Map([[targetPath, cloned as unknown]]), 'copy-action').subscribe({
			next: () => {
				this.markDocumentSaved(targetPath, json);
				this.messageService.add({
					severity: 'success', summary: 'Action copied',
					detail: `Copied to scene "${target.slug}" as "${newAction.slug}".${seekHint}`,
				});
			},
			error: (err) => {
				this.markDocumentDirty(targetPath, json);
				this.messageService.add({
					severity: 'error', summary: 'Copy failed',
					detail: err?.error?.error ?? `Could not save scene "${target.slug}".`,
				});
			},
		});
	}

	// ── Copy scene / image to another chapter ───────────────────────────────
	protected onCopyScene(event: { chapterSlug: string | null; sceneSlug: string }): void {
		if (this.stateService.isReadOnly()) return;
		this.copyEntitySource.set({ kind: 'scene', chapterSlug: event.chapterSlug, slug: event.sceneSlug });
		this.copyChapterDialogVisible.set(true);
	}

	protected onCopyImage(event: { chapterSlug: string | null; imageSlug: string }): void {
		if (this.stateService.isReadOnly()) return;
		this.copyEntitySource.set({ kind: 'image', chapterSlug: event.chapterSlug, slug: event.imageSlug });
		this.copyChapterDialogVisible.set(true);
	}

	protected onCopyChapterTargetSelected(target: CopyChapterSelection): void {
		// The dialog is mounted outside the read-only fieldset, so it needs its
		// own guard rather than relying on the disabled propagation.
		if (this.stateService.isReadOnly()) return;
		const source = this.copyEntitySource();
		if (!source) return;
		this.copyEntitySource.set(null);
		if (source.kind === 'scene') this.copySceneTo(target.chapterSlug, source.chapterSlug, source.slug);
		else this.copyImageTo(target.chapterSlug, source.chapterSlug, source.slug);
	}

	private scenePathFor(chapterSlug: string | null, sceneSlug: string): string {
		return chapterSlug
			? `/projects/${this.slug}/chapters/${chapterSlug}/scenes/${sceneSlug}/scene.json`
			: `/projects/${this.slug}/scenes/${sceneSlug}/scene.json`;
	}

	private imagePathFor(chapterSlug: string | null, imageSlug: string): string {
		return chapterSlug
			? `/projects/${this.slug}/chapters/${chapterSlug}/images/${imageSlug}/image.json`
			: `/projects/${this.slug}/images/${imageSlug}/image.json`;
	}

	private copyFailed(detail: string): void {
		this.messageService.add({ severity: 'error', summary: 'Copy failed', detail });
	}

	private destinationLabel(chapterSlug: string | null): string {
		if (!chapterSlug) return 'project level';
		const chapter = (this.projectData()?.chapters ?? []).find(c => c.slug === chapterSlug);
		return `chapter "${chapter?.name?.trim() || chapterSlug}"`;
	}

	private copySceneTo(destChapter: string | null, srcChapter: string | null, srcSlug: string): void {
		const project = this.projectData();
		if (!project) return;
		const siblings = scenesOf(project, srcChapter);
		const sourceIndex = siblings.findIndex(r => r.slug === srcSlug);
		const sourceRef = siblings[sourceIndex];
		if (!sourceRef) {
			this.copyFailed(`Scene "${srcSlug}" is no longer in the project.`);
			return;
		}

		const sourceSlugs = splitSlugsFor(sourceRef);
		const siblingsOfDest = destChapter === srcChapter ? siblings : scenesOf(project, destChapter);
		const newSlug = uniqueSceneSlug(sourceRef.slug, sourceRef.split, this.slugsService.takenSceneSlugs(siblingsOfDest, destChapter));
		const targetSlugs = splitSlugsFor({ slug: newSlug, split: sourceRef.split });
		const refId = generateEntityId();

		forkJoin(
			sourceSlugs.map(slug => this.loadTargetContent(this.scenePathFor(srcChapter, slug), 'scene')),
		).subscribe({
			// A split family is all-or-nothing — a partially written set would
			// leave the ref pointing at scenes that don't exist.
			error: () => this.copyFailed(`Could not load scene "${srcSlug}".`),
			next: (docs) => {
				const files = new Map<string, unknown>();
				docs.forEach((doc, i) => {
					const path = this.scenePathFor(destChapter, targetSlugs[i]);
					// A plain scene shares its id with its ref; split children each own one.
					const docId = sourceSlugs.length === 1 ? refId : generateEntityId();
					const cloned = cloneSceneDocForCopy(doc as SceneData, targetSlugs[i], docId);
					files.set(path, cloned);
					// Seed the cache before project.json changes, or the timeline's
					// signal-driven refetch races the PUT and 404s.
					this.stateService.setScene(path, cloned);
				});

				const newRef = cloneSceneRefForCopy(sourceRef, newSlug, refId);
				const insertAt = srcChapter === destChapter ? sourceIndex + 1 : undefined;
				this.insertSceneRef(destChapter, newRef, insertAt);

				this.dataService.saveFiles(files, 'copy-scene', { create: true }).pipe(retry({ count: 1, delay: 500 })).subscribe({
					next: () => this.messageService.add({
						severity: 'success', summary: 'Scene copied',
						detail: `Copied to ${this.destinationLabel(destChapter)} as "${newSlug}".`,
					}),
					error: (err) => {
						// A ref with no scene doc behind it 404s the player at startup.
						this.removeSceneRef(destChapter, newSlug);
						this.copyFailed(err?.error?.error ?? `Could not save scene "${newSlug}".`);
					},
				});
			},
		});
	}

	private copyImageTo(destChapter: string | null, srcChapter: string | null, srcSlug: string): void {
		const project = this.projectData();
		if (!project) return;
		const siblings = imagesOf(project, srcChapter);
		const sourceIndex = siblings.findIndex(r => r.slug === srcSlug);
		const sourceRef = siblings[sourceIndex];
		if (!sourceRef) {
			this.copyFailed(`Image "${srcSlug}" is no longer in the project.`);
			return;
		}

		const taken = this.slugsService.takenImageSlugs(imagesOf(project, destChapter), destChapter);
		const newSlug = makeUniqueSlug(deriveSlug(sourceRef.slug) || 'image', taken);
		const newId = generateEntityId();

		this.loadTargetContent(this.imagePathFor(srcChapter, srcSlug), 'image').subscribe({
			error: () => this.copyFailed(`Could not load image "${srcSlug}".`),
			next: (doc) => {
				const path = this.imagePathFor(destChapter, newSlug);
				const cloned = cloneImageDocForCopy(doc as ImageData, newSlug, newId);
				this.stateService.setImage(path, cloned);

				const newRef = cloneImageRefForCopy(sourceRef, newSlug, newId);
				const insertAt = srcChapter === destChapter ? sourceIndex + 1 : undefined;
				this.insertImageRef(destChapter, newRef, insertAt);

				this.dataService.saveFiles(new Map([[path, cloned as unknown]]), 'copy-image', { create: true }).pipe(retry({ count: 1, delay: 500 })).subscribe({
					next: () => this.messageService.add({
						severity: 'success', summary: 'Image copied',
						detail: `Copied to ${this.destinationLabel(destChapter)} as "${newSlug}".`,
					}),
					error: (err) => {
						this.removeImageRef(destChapter, newSlug);
						this.copyFailed(err?.error?.error ?? `Could not save image "${newSlug}".`);
					},
				});
			},
		});
	}

	/** Mutates the destination ref list in a `projectData` clone and republishes it. */
	private patchRefList(
		chapterSlug: string | null,
		kind: 'scenes' | 'images',
		mutate: (list: Record<string, unknown>[]) => void,
	): void {
		const current = this.projectData();
		if (!current) return;
		const project = cloneDeep(current);
		const container = chapterSlug
			? (project.chapters ?? []).find(c => c.slug === chapterSlug)
			: project;
		if (!container) return;
		const holder = container as unknown as Record<string, unknown>;
		if (!Array.isArray(holder[kind])) holder[kind] = [];
		mutate(holder[kind] as Record<string, unknown>[]);
		this.onTimelineDataChange(project);
	}

	private insertSceneRef(chapterSlug: string | null, ref: SceneRef, index?: number): void {
		this.patchRefList(chapterSlug, 'scenes', list => {
			const entry = ref as unknown as Record<string, unknown>;
			if (index === undefined) list.push(entry);
			else list.splice(index, 0, entry);
		});
	}

	private removeSceneRef(chapterSlug: string | null, slug: string): void {
		this.patchRefList(chapterSlug, 'scenes', list => {
			const i = list.findIndex(r => r['slug'] === slug);
			if (i >= 0) list.splice(i, 1);
		});
	}

	private insertImageRef(chapterSlug: string | null, ref: ImageRef, index?: number): void {
		this.patchRefList(chapterSlug, 'images', list => {
			const entry = ref as unknown as Record<string, unknown>;
			if (index === undefined) list.push(entry);
			else list.splice(index, 0, entry);
		});
	}

	private removeImageRef(chapterSlug: string | null, slug: string): void {
		this.patchRefList(chapterSlug, 'images', list => {
			const i = list.findIndex(r => r['slug'] === slug);
			if (i >= 0) list.splice(i, 1);
		});
	}

	// ── Slug rename handlers ────────────────────────────────────────────────
	protected onSceneSlugRenamed(event: { oldSlug: string; newSlug: string }): void {
		const chapterSlug = this.getActiveChapterSlug() ?? undefined;
		this.projectsService.renameSlug(this.slug, {
			type: 'scene', oldSlug: event.oldSlug, newSlug: event.newSlug, chapterSlug,
		}).subscribe({
			next: () => {
				const scenePath = chapterSlug
					? `/projects/${this.slug}/chapters/${chapterSlug}/scenes/${event.newSlug}/scene.json`
					: `/projects/${this.slug}/scenes/${event.newSlug}/scene.json`;
				this.reloadAndNavigate(scenePath);
			},
		});
	}

	protected onImageSlugRenamed(event: { oldSlug: string; newSlug: string }): void {
		const chapterSlug = this.getActiveChapterSlug() ?? undefined;
		this.projectsService.renameSlug(this.slug, {
			type: 'image', oldSlug: event.oldSlug, newSlug: event.newSlug, chapterSlug,
		}).subscribe({
			next: () => {
				const imagePath = chapterSlug
					? `/projects/${this.slug}/chapters/${chapterSlug}/images/${event.newSlug}/image.json`
					: `/projects/${this.slug}/images/${event.newSlug}/image.json`;
				this.reloadAndNavigate(imagePath);
			},
		});
	}

	protected onChapterSlugRenamed(event: { oldSlug: string; newSlug: string }): void {
		this.projectsService.renameSlug(this.slug, {
			type: 'chapter', oldSlug: event.oldSlug, newSlug: event.newSlug,
		}).subscribe({
			next: () => this.reloadAndNavigate(`chapter:${event.newSlug}`),
		});
	}

	private reloadAndNavigate(target?: string): void {
		const projectPath = `/projects/${this.slug}/project.json`;
		this.dataService.loadFile<ProjectData>(projectPath).subscribe({
			next: (project) => {
				this.projectData.set(project);
				this.originalProjectJson = JSON.stringify(project, null, 2);
				this.filesService.load(this.slug);
				// The only caller is a slug rename, which moves document keys
				// under the occupied map — re-read it rather than track the move.
				this.slugsService.load(this.slug);
				if (target?.startsWith('chapter:')) {
					this.selectChapter(target.substring('chapter:'.length));
				} else if (target) {
					this.selectFile(target);
				} else {
					this.selectFile(projectPath);
				}
			},
		});
	}

	/** Incremented to force timeline to re-fetch scene data (e.g. after framesCount change). */
	protected timelineVersion = signal(0);

	private applyUrlRewriteToForm(prefix: string, canonicalUrl: string): void {
		if (!prefix || prefix === canonicalUrl) return;
		const current = this.formData();
		if (!current) return;
		const cloned = cloneDeep(current);
		if (!canonicalizeUrlsDeep(cloned, prefix, canonicalUrl)) return;
		this.formData.set(cloned);
		const json = JSON.stringify(cloned, null, 2);
		this.editorContent.set(json);
		const dirty = json !== this.loadedContent;
		this.editorDirty.set(dirty);
		this.trackPending(this.selectedPath(), dirty, json);
	}

	private applyPlaybackTypeClearToForm(mediaUrl: string): void {
		if (this.selectedNodeType() !== 'scene') return;
		const current = this.formData() as Record<string, unknown> | undefined;
		if (!current) return;
		if (current['playbackType'] !== 'VIDEO_NP') return;
		const bg = current['background'] as { source?: { src?: unknown } } | undefined;
		if (bg?.source?.src !== mediaUrl) return;
		const cloned = cloneDeep(current);
		delete (cloned as Record<string, unknown>)['playbackType'];
		this.formData.set(cloned);
		const json = JSON.stringify(cloned, null, 2);
		this.editorContent.set(json);
		const dirty = json !== this.loadedContent;
		this.editorDirty.set(dirty);
		this.trackPending(this.selectedPath(), dirty, json);
	}

	protected onFormChange(updated: ProjectData | Record<string, unknown>): void {
		this.formData.set(updated);

		const json = JSON.stringify(updated, null, 2);
		this.editorContent.set(json);
		const dirty = json !== this.loadedContent;
		this.editorDirty.set(dirty);
		this.trackPending(this.selectedPath(), dirty, json);
		if (this.selectedNodeType() === 'project') {
			this.projectData.set(updated as ProjectData);
		}
	}

	/**
	 * The `<webout-player>` is an imperatively-created custom element inside
	 * player-panel's template; an HMR template/style swap recreates that container
	 * empty and drops the player (previously requiring a manual F5). Re-mount whenever
	 * the view (re)initialises. Guarded on config readiness so the initial emit before
	 * config loads is a no-op — the mount effect handles that first mount once ready.
	 */
	protected onPlayerViewReady(): void {
		if (this.configService.ready()) this.remountPlayer();
	}

	protected onChapterDropdownChange(idx: number): void {
		this.selectedChapterIndex.set(idx);
		this.remountPlayer();
		this.playerPanel()?.resetZoom();
	}

	/**
	 * Fires both on organic chapter transitions during playback and after an
	 * explicit `selectChapter` remount — a stale zoom/pan from the previous
	 * chapter's frame is disorienting once the content underneath has changed
	 * (DEV-64), so reset unconditionally rather than trying to distinguish
	 * "same chapter, still playing" from "actually changed".
	 */
	protected onActiveChapterChange(chapterSlug: string): void {
		const idx = this.chapters().findIndex(ch => ch.slug === chapterSlug);
		if (idx >= 0) {
			this.selectedChapterIndex.set(idx);
		}
		this.playerPanel()?.resetZoom();
	}

	protected onChapterSelectFromGraph(event: { chapterSlug: string }): void {
		const idx = this.chapters().findIndex(ch => ch.slug === event.chapterSlug);
		if (idx >= 0) this.selectedChapterIndex.set(idx);
	}

	protected onSeek(timeMs: number): void {
		this.playerPanel()?.seek(timeMs);
	}

	/** `insertIndex` is a RAW `scenes` position — see `onTimelineAudioRemove`. */
	protected onTimelineFileDrop(event: { type: 'video' | 'audio'; baseUrl: string; name: string; durationSeconds?: number; fps?: number; insertIndex?: number; dataType?: 'VIDEO' | 'IMAGE' }): void {
		const project = this.projectData();
		if (!project) return;

		const clone = cloneDeep(project);
		const chapterIdx = this.selectedChapterIndex();
		const activeChapter = clone.chapters?.[chapterIdx];

		if (event.type === 'video') {
			// Create a new scene from the dropped video/image
			const isImage = event.dataType === 'IMAGE';
			// One lookup for both the VIDEO_NP stamp and the generation trigger
			// below: the flag is only cleared by a generation job's completion
			// event, so stamping a media that will not generate leaves the scene
			// black for good (DEV-530).
			const droppedMedia = this.filesService.files().find(f => f.baseUrl === event.baseUrl);
			const name = event.name.replace(/\.[^.]+$/, '');
			// Refs ∪ documents — an orphaned scene document under this slug would
			// otherwise be silently overwritten by the create below (DEV-670).
			const existingSlugs = this.slugsService.takenSceneSlugs(
				(activeChapter ? activeChapter.scenes : clone.scenes) ?? [],
				activeChapter?.slug ?? null,
			);
			const slug = makeUniqueSlug(deriveSlug(name) || 'scene', existingSlugs);
			const fps = event.fps || (project.fps ?? 25);
			// Images have no probed duration — default to a 3s end-screen-style
			// hold, immediately editable via the scene-form's Duration field.
			const framesCount = event.durationSeconds
				? Math.round(event.durationSeconds * fps)
				: (isImage ? Math.round(3 * fps) : 0);
			const id = generateEntityId();

			const sceneData = {
				id, name, slug, framesCount,
				// playbackType (VIDEO_NP) only applies to actual video sources whose
				// versions are still being generated — never to a static image
				// background, and never to a media that is already generated, since
				// nothing would ever clear the flag again (DEV-530).
				...(!isImage && shouldStampVideoNp(droppedMedia) ? { playbackType: 'VIDEO_NP' } : {}),
				background: { source: { src: event.baseUrl, srcType: 'ABSOLUTE', dataType: isImage ? 'IMAGE' : 'VIDEO' } },
				content: [],
			};

			// Scene ref carries id + slug only; name stays on the scene doc
			let scenePath: string;
			const sceneRef = event.baseUrl.includes('[VARIANT]')
				? { id, slug, variant: { type: 'NAME' as const, key: '', namesMapKey: deriveNamesMapKey(event.name) } }
				: { id, slug };
			if (activeChapter) {
				const chapter = activeChapter;
				if (!chapter.scenes) chapter.scenes = [];
				if (event.insertIndex != null) {
					chapter.scenes.splice(event.insertIndex, 0, sceneRef);
				} else {
					chapter.scenes.push(sceneRef);
				}
				scenePath = `/projects/${this.slug}/chapters/${chapter.slug}/scenes/${slug}/scene.json`;
			} else {
				if (!clone.scenes) clone.scenes = [];
				if (event.insertIndex != null) {
					clone.scenes.splice(event.insertIndex, 0, sceneRef);
				} else {
					clone.scenes.push(sceneRef);
				}
				scenePath = `/projects/${this.slug}/scenes/${slug}/scene.json`;
			}

			// Populate namesMap for variant groups
			if (event.baseUrl.includes('[VARIANT]')) {
				this.populateNamesMapFromGroup(clone, event.baseUrl);
			}

			// Seed the in-memory cache so the timeline's signal-driven re-fetch
			// can resolve the new scene from `peekScene()` instead of racing
			// the fire-and-forget PUT below with an HTTP GET that 404s and
			// wipes every other scene block via `forkJoin` rejection.
			this.stateService.setScene(scenePath, sceneData as unknown as import('@webout/types').SceneData);
			this.projectData.set(clone);
			this.onTimelineDataChange(clone);
			this.dataService.saveFiles(new Map([[scenePath, sceneData]]), 'timeline-drop', { create: true }).subscribe();
			this.autoGenerateForMedia(event.baseUrl, droppedMedia);

			// Image scenes need a manually-set duration (no probed length exists)
			// — jump straight to the new scene and focus the Duration field so
			// the user can fix the 3s default immediately, per PRO-196.
			if (isImage) {
				this.selectSceneFromForm({ chapterSlug: activeChapter?.slug ?? null, sceneSlug: slug });
				setTimeout(() => {
					document.querySelector<HTMLInputElement>('[data-scene-duration-input] input')?.focus();
				}, 50);
			}
		} else {
			// Add audio track entry
			const audioName = event.name.replace(/\.[^.]+$/, '');
			const existingAudioSlugs = new Set(activeChapter
				? (activeChapter.audioTrack ?? []).map((a: { slug: string }) => a.slug)
				: (clone.audioTrack ?? []).map((a: { slug: string }) => a.slug));
			const audioSlug = makeUniqueSlug(deriveSlug(audioName) || 'audio', existingAudioSlugs);
			const audioEntry: Record<string, unknown> = { id: generateEntityId(), name: audioName, slug: audioSlug, src: event.baseUrl, srcType: 'ABSOLUTE' };
			if (event.baseUrl.includes('[VARIANT]')) {
				audioEntry['variant'] = { type: 'NAME', key: '', namesMapKey: deriveNamesMapKey(event.name) };
			}

			if (activeChapter) {
				if (!activeChapter.audioTrack) activeChapter.audioTrack = [];
				activeChapter.audioTrack.push(audioEntry as never);
			} else {
				if (!clone.audioTrack) clone.audioTrack = [];
				clone.audioTrack.push(audioEntry as never);
			}

			// Populate namesMap for variant groups
			if (event.baseUrl.includes('[VARIANT]')) {
				this.populateNamesMapFromGroup(clone, event.baseUrl);
			}

			this.projectData.set(clone);
			this.onTimelineDataChange(clone);
			this.autoGenerateForMedia(event.baseUrl);
		}
	}

	protected onTimelineSceneRemove(event: { chapterSlug: string | null; sceneSlug: string }): void {
		const project = this.projectData();
		if (!project) return;

		const clone = cloneDeep(project);

		// Remove scene ref from project data
		if (event.chapterSlug) {
			const chapter = clone.chapters?.find(c => c.slug === event.chapterSlug);
			if (chapter?.scenes) {
				chapter.scenes = chapter.scenes.filter(s => s.slug !== event.sceneSlug);
			}
		} else if (clone.scenes) {
			clone.scenes = clone.scenes.filter(s => s.slug !== event.sceneSlug);
		}

		this.onTimelineDataChange(clone);

		// Delete the scene document
		this.projectsService.deleteScene(this.slug, event.sceneSlug, event.chapterSlug ?? undefined)
			.subscribe(() => this.slugsService.forgetScene(event.chapterSlug, event.sceneSlug));
	}

	/**
	 * `index` is the ref's position in the RAW `audioTrack` array, not the
	 * position of the block that was clicked — the timeline hides `alt` refs,
	 * variant-gated refs, refs whose start scene is filtered out and refs whose
	 * media has no known duration, so the two disagree in 24 of the projects in
	 * the catalogue. The panel translates it; this splice must stay raw.
	 */
	protected onTimelineAudioRemove(event: { index: number }): void {
		const project = this.projectData();
		if (!project) return;

		const clone = cloneDeep(project);
		const chapterIdx = this.selectedChapterIndex();
		const activeChapter = clone.chapters?.[chapterIdx];

		if (activeChapter) {
			if (activeChapter.audioTrack) {
				activeChapter.audioTrack.splice(event.index, 1);
			}
		} else if (clone.audioTrack) {
			clone.audioTrack.splice(event.index, 1);
		}

		this.onTimelineDataChange(clone);
	}

	protected onTimelineDataChange(updated: ProjectData): void {
		this.projectData.set(updated);
		const projectPath = `/projects/${this.slug}/project.json`;
		const json = JSON.stringify(updated, null, 2);
		const dirty = json !== this.originalProjectJson;
		this.trackPending(projectPath, dirty, json);
		this.syncFormFromProject();
	}

	// ── Methods: private ────────────────────────────────────────────────────
	private buildFetchPathFromParams(chapter: string | null, scene: string | null, image: string | null): string | null {
		if (scene && chapter) {
			return `/projects/${this.slug}/chapters/${chapter}/scenes/${scene}/scene.json`;
		}
		if (scene) {
			return `/projects/${this.slug}/scenes/${scene}/scene.json`;
		}
		if (image && chapter) {
			return `/projects/${this.slug}/chapters/${chapter}/images/${image}/image.json`;
		}
		if (image) {
			return `/projects/${this.slug}/images/${image}/image.json`;
		}
		return null;
	}

	/** Set when a remount was skipped because a referenced audio's codec is still generating; the readiness effect re-fires the remount once it clears. */
	private deferredAudioRemount = false;

	/**
	 * DEV-275: active generation jobs for media the current preview mount references —
	 * video backgrounds of mounted scenes still flagged `VIDEO_NP` (the player renders
	 * them black until decoded) plus mounted audio tracks (their aac is still
	 * transcoding). Drives the player's non-blocking "generating" overlay so that
	 * hitting play the instant a just-dropped clip is generating doesn't look broken.
	 * Recomputes on every job poll and on scene edits, so it clears the moment the
	 * relevant jobs finish.
	 */
	readonly waitingMediaJobs = computed<JobListItem[]>(() => {
		const active = this.jobsService.activeByMediaId();
		if (active.size === 0) return [];
		const mounted = new Set(chaptersToMount(
			this.chapters().map(c => c.slug),
			this.chapters()[this.selectedChapterIndex()]?.slug,
		));

		const srcs = new Set<string>();
		const project = this.projectData();
		if (project) {
			const tracks: { src?: string }[] = [...(project.audioTrack ?? [])];
			for (const ch of project.chapters ?? []) {
				if (mounted.has(ch.slug)) tracks.push(...(ch.audioTrack ?? []));
			}
			for (const t of tracks) if (t?.src) srcs.add(t.src);
		}
		for (const src of this.stateService.mountedVideoNpBackgrounds(mounted)) srcs.add(src);

		const jobs: JobListItem[] = [];
		const seen = new Set<string>();
		for (const src of srcs) {
			const id = this.filesService.findByBaseUrl(src)?.id;
			const job = id != null ? active.get(id) : undefined;
			if (job && !seen.has(job.jobId)) { seen.add(job.jobId); jobs.push(job); }
		}
		return jobs;
	});

	/** DEV-275: 'generating' while `waitingMediaJobs` is non-empty — bound to the player overlay. */
	readonly playerGenerationState = computed<'idle' | 'generating'>(() =>
		this.waitingMediaJobs().length > 0 ? 'generating' : 'idle');

	/** Set by a dimensions reset; cleared once the save round-trip refreshes the refs. */
	private imageRefDimensionsReset = false;

	/**
	 * Re-read the image refs the backend just filled in.
	 *
	 * Only after a reset, and only the two dimension fields are copied across —
	 * a wholesale `projectData.set(fetched)` would discard anything edited
	 * during the round trip, which is why the save endpoint's own response
	 * (`{ results: [...] }`, no content) is not enough here.
	 */
	private refreshImageRefDimensions(): void {
		if (!this.imageRefDimensionsReset) return;
		this.imageRefDimensionsReset = false;
		const projectPath = `/projects/${this.slug}/project.json`;
		this.dataService.loadFile<ProjectData>(projectPath)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (fetched) => {
					const current = this.projectData();
					if (!current) return;
					const derived = new Map<string, Record<string, unknown>>();
					const collect = (refs: unknown, scope: string): void => {
						for (const entry of (refs as Record<string, unknown>[] | undefined) ?? []) {
							if (entry && typeof entry === 'object') derived.set(`${scope}/${String(entry['slug'])}`, entry);
						}
					};
					collect(fetched.images, '');
					for (const chapter of fetched.chapters ?? []) collect((chapter as unknown as Record<string, unknown>)['images'], chapter.slug);

					const project = cloneDeep(current);
					let changed = false;
					const apply = (refs: unknown, scope: string): void => {
						for (const entry of (refs as Record<string, unknown>[] | undefined) ?? []) {
							if (!entry || typeof entry !== 'object') continue;
							const source = derived.get(`${scope}/${String(entry['slug'])}`);
							if (!source) continue;
							for (const field of ['dimensions', 'origCoordsWidth']) {
								if (entry[field] === undefined && source[field] !== undefined) {
									entry[field] = source[field];
									changed = true;
								}
							}
						}
					};
					apply(project.images, '');
					for (const chapter of project.chapters ?? []) apply((chapter as unknown as Record<string, unknown>)['images'], chapter.slug);
					if (!changed) return;

					// Written straight to the signal, NOT through `trackPending`:
					// these are the values the document already holds, so queuing
					// them would mark the project dirty against itself.
					this.projectData.set(project);
					this.originalProjectJson = JSON.stringify(project, null, 2);
				},
			});
	}

	/** The chapter the player would really start on — see `firstAvailableChapterIndex`. */
	private availableChapterIndex(): number {
		return firstAvailableChapterIndex(
			this.chapters() as unknown as { attributes?: Record<string, unknown> }[],
			this.currentArgsMap(),
			this.selectedChapterIndex(),
			attributesMatch,
		);
	}

	private remountPlayer(): void {
		const panel = this.playerPanel();
		if (!panel) return;

		// DEV-809: a freshly-dropped audio kicks off async codec (aac) generation.
		// Mounting before it finishes makes the autonomous <webout-player> fetch
		// project.json, derive the not-yet-existing `.aac` and hard-crash on WebAudio
		// decode (video doesn't hit this — it streams the always-present original).
		// Defer the mount; the readiness effect re-fires it once the job clears.
		if (this.hasUnreadyReferencedAudio()) {
			this.deferredAudioRemount = true;
			return;
		}
		this.deferredAudioRemount = false;

		// DEV-833: gating a chapter can strand the selection on one the player
		// skips. Follow the player to the chapter it would really start on, so
		// the timeline and the preview never show different chapters; if nothing
		// matches, say so instead of mounting a player that cannot initialise.
		if (this.chapters().length) {
			const available = this.availableChapterIndex();
			if (available < 0) {
				panel.showBlocked('Every chapter is skipped by the current argument values. Set an argument that matches one to preview it.');
				return;
			}
			if (available !== this.selectedChapterIndex()) this.selectedChapterIndex.set(available);
		}

		const mountList = chaptersToMount(
			this.chapters().map(c => c.slug),
			this.chapters()[this.selectedChapterIndex()]?.slug,
		);

		panel.remount(
			mountList,
			this.configService.buildArgsMap(),
			this.configService.buildVarsMap(),
			this.configService.region(),
			// The editor previews the working copy, which only this backend serves.
			environment.backendUrl,
		);
		this.configService.snapshotMounted();
	}

	/**
	 * Whether any audio track the upcoming mount would expose (project-level +
	 * the mounted chapters') points at a local media whose codec job is still
	 * running — i.e. its `.aac` sibling doesn't exist yet. Gated on the ACTIVE
	 * job set, not on version count, so it only defers during real generation and
	 * never permanently hides an already-generated (or externally-hosted) track.
	 * See DEV-809.
	 */
	private hasUnreadyReferencedAudio(): boolean {
		const active = this.jobsService.activeByMediaId();
		if (active.size === 0) return false;
		const project = this.projectData();
		if (!project) return false;
		const mounted = new Set(chaptersToMount(
			this.chapters().map(c => c.slug),
			this.chapters()[this.selectedChapterIndex()]?.slug,
		));
		const tracks: { src?: string }[] = [...(project.audioTrack ?? [])];
		for (const ch of project.chapters ?? []) {
			if (mounted.has(ch.slug)) tracks.push(...(ch.audioTrack ?? []));
		}
		return tracks.some(t => {
			if (!t?.src) return false;
			const id = this.filesService.findByBaseUrl(t.src)?.id;
			return id != null && active.has(id);
		});
	}

	private loadProjectData(): void {
		this.dataService.loadFile<ProjectData>(`/projects/${this.slug}/project.json`).subscribe({
			next: (project) => {
				this.projectData.set(project);
				this.originalProjectJson = JSON.stringify(project, null, 2);
				this.filesService.load(this.slug);
				this.jobsService.loadActiveJobs(this.slug);
				// Which slugs the collections hold, including documents no ref
				// points at — the other half of every `taken` set (DEV-670).
				this.slugsService.load(this.slug);
				// Also populate the shared state service
				this.stateService.projectSlug.set(this.slug);
				this.stateService.projectData.set(project);
				// DEV-810: an open project/chapter form renders `formData`, which
				// this reload would otherwise leave listing scenes the backend has
				// already deleted (the media-delete cascade behind
				// `onTimelineChangedExternally`). Skipped while project.json has
				// unsaved edits queued — replacing them with the server copy would
				// drop work that is still on its way to a PUT.
				if (!this.pendingChanges().has(`/projects/${this.slug}/project.json`)) {
					this.syncFormFromProject(false);
				}
				this.leaveSelectionIfItVanished(project);
			},
			error: () => { /* project data unavailable */ },
		});
		// Fetch project metadata (sourceUrl etc.) separately — project.json is the
		// player-facing content only; sourceUrl lives on the Mongo top-level.
		this.projectsService.getProjectMeta(this.slug).subscribe({
			next: (meta) => this.stateService.projectSourceUrl.set(meta.sourceUrl ?? undefined),
			error: () => { /* meta unavailable — treat as non-imported (export menu shows both) */ },
		});
	}

	private inferNodeType(fetchPath: string): 'project' | 'scene' | 'image' {
		if (fetchPath.includes('/scenes/')) return 'scene';
		if (fetchPath.includes('/images/')) return 'image';
		return 'project';
	}

	private updateUrl(fetchPath: string): void {
		const matrix = this.buildMatrixParams(fetchPath);
		this.navigateToEditorState(matrix);
	}

	private buildMatrixParams(fetchPath: string): Record<string, string> {
		const sceneMatch = fetchPath.match(/\/chapters\/([^/]+)\/scenes\/([^/]+)\//);
		if (sceneMatch) {
			return { chapter: sceneMatch[1], scene: sceneMatch[2] };
		}
		const chapterImageMatch = fetchPath.match(/\/chapters\/([^/]+)\/images\/([^/]+)\//);
		if (chapterImageMatch) {
			return { chapter: chapterImageMatch[1], image: chapterImageMatch[2] };
		}
		const projectSceneMatch = fetchPath.match(/\/projects\/[^/]+\/scenes\/([^/]+)\//);
		if (projectSceneMatch) {
			return { scene: projectSceneMatch[1] };
		}
		const projectImageMatch = fetchPath.match(/\/projects\/[^/]+\/images\/([^/]+)\//);
		if (projectImageMatch) {
			return { image: projectImageMatch[1] };
		}
		return {};
	}

	private getActiveChapterSlug(): string | null {
		const path = this.selectedPath();
		if (!path) return null;
		if (path.startsWith('chapter:')) return path.substring('chapter:'.length);
		// Extract chapter slug from scene/image paths like /projects/.../chapters/{ch}/scenes/...
		const chapterMatch = path.match(/\/chapters\/([^/]+)\//);
		return chapterMatch ? chapterMatch[1] : null;
	}

	/**
	 * Mirror `projectData` into an open project/chapter form.
	 *
	 * `markDirty` distinguishes the two callers: a local edit (the timeline)
	 * leaves unsaved work behind and must mark the form dirty, while a reload
	 * (`loadProjectData`) brought the server's own copy back — that IS the saved
	 * baseline, so re-point `loadedContent` at it instead of raising a phantom
	 * unsaved-changes prompt and re-PUTting unchanged content.
	 */
	private syncFormFromProject(markDirty = true): void {
		const project = this.projectData();
		if (!project) return;

		const nodeType = this.selectedNodeType();
		if (nodeType === 'project') {
			const json = JSON.stringify(project, null, 2);
			this.formData.set(project);
			this.editorContent.set(json);
			if (markDirty) {
				this.editorDirty.set(true);
			} else {
				this.loadedContent = json;
				this.editorDirty.set(false);
			}
		} else if (nodeType === 'chapter') {
			const chapterSlug = this.getActiveChapterSlug();
			if (chapterSlug) {
				const chapter = (project.chapters ?? []).find(c => c.slug === chapterSlug);
				if (chapter) {
					const json = JSON.stringify(chapter, null, 2);
					this.formData.set(chapter);
					this.editorContent.set(json);
					if (!markDirty) {
						this.loadedContent = json;
						this.editorDirty.set(false);
					}
				}
			}
		}
	}

	/**
	 * DEV-810: mirror a scene/image document written from OUTSIDE the form back
	 * into `formData` — the form's own parallel copy of the document that
	 * `EditorStateService` holds. Without the mirror the write is invisible
	 * until a re-navigation, and, worse, `scene-form`/`image-form`'s `patch()`
	 * clones the stale `data()` on the user's next keystroke and silently
	 * reverts the write — first in the cache, then in the database via the
	 * auto-save PUT.
	 *
	 * `loadedContent` is deliberately left pointing at the server baseline here:
	 * the write's own PUT is still in flight, and `markDocumentSaved()` /
	 * `markDocumentDirty()` settle the baseline once it answers.
	 *
	 * No-op unless the written document is the one currently open — the copy
	 * dialogs can target any scene/image in the project.
	 */
	/**
	 * `/projects/<p>/chapters/<ch>/scenes/<sc>/scene.json` → `{ chapterSlug: 'ch', slug: 'sc' }`.
	 * A project-level document has no `/chapters/` segment and yields a null chapter.
	 * Returns null for anything that is not a scene/image document path.
	 */
	private parseDocPath(path: string): { chapterSlug: string | null; slug: string } | null {
		const m = /\/projects\/[^/]+(?:\/chapters\/([^/]+))?\/(scenes|images)\/([^/]+)\/(?:scene|image)\.json$/.exec(path);
		if (!m) return null;
		return { chapterSlug: m[1] ?? null, slug: m[3] };
	}

	/**
	 * Leave a scene/image whose ref no longer exists in the freshly loaded
	 * project, falling back to its chapter and then to the project.
	 *
	 * Deleting a media that is a scene's primary video background takes the whole
	 * scene with it — `cleanMediaReferences` journals a snapshot, then
	 * `SceneModel.deleteOne` plus `stripSceneRef`, so the document is gone from
	 * both Mongo and `project.json`. The timeline reflects that immediately, but
	 * the right-hand panel kept rendering a form for a document that no longer
	 * exists anywhere.
	 *
	 * That is not merely stale: the next keystroke queues a PUT for a path with no
	 * ref, which the batch route's reachability gate refuses — surfacing as a
	 * spurious "changes are not saved" (the DEV-663 symptom) over an edit that
	 * could never have landed. Any pending entry for the vanished document is
	 * dropped for the same reason; it is unsaveable by construction, and the
	 * pre-delete snapshot is recoverable from the save journal.
	 */
	/**
	 * Every scene slug a document could legitimately be open under, anywhere in the
	 * project. Deliberately global rather than per-chapter, because the selected
	 * path does not always name the chapter that holds the ref: an
	 * `externalChapter` ref is selected under its *target* chapter while the ref
	 * itself lives in the borrowing one. Split refs fan out too — the timeline
	 * selects a part as `<slug>__<i>` (`timeline-panel.ts:502`, the same asymmetry
	 * `:408` maps back through `splitParent`) while `project.json` carries only the
	 * base. Matching narrowly would navigate away from perfectly valid documents.
	 */
	private allSceneSlugs(project: ProjectData): Set<string> {
		const out = new Set<string>();
		const add = (refs: SceneRef[] | undefined): void => {
			for (const r of refs ?? []) {
				out.add(r.slug);
				const split = typeof r.split === 'number' ? r.split : 0;
				for (let i = 0; i < split; i++) out.add(`${r.slug}__${i}`);
				if (typeof r.externalChapter === 'string') {
					const slash = r.externalChapter.indexOf('/');
					if (slash > 0) out.add(r.externalChapter.slice(slash + 1));
				}
			}
		};
		add(project.scenes);
		for (const c of project.chapters ?? []) add(c.scenes);
		return out;
	}

	/** Same idea for images, which have neither split nor externalChapter. */
	private allImageSlugs(project: ProjectData): Set<string> {
		const out = new Set<string>();
		for (const r of project.images ?? []) out.add(r.slug);
		for (const c of project.chapters ?? []) for (const r of c.images ?? []) out.add(r.slug);
		return out;
	}

	/**
	 * Leave a scene/image whose document has vanished from the freshly loaded
	 * project, falling back to its chapter and then to the project.
	 *
	 * Deleting a media that is a scene's primary video background takes the whole
	 * scene with it — `cleanMediaReferences` journals a snapshot, then
	 * `SceneModel.deleteOne` plus `stripSceneRef`, so the document is gone from
	 * both Mongo and `project.json`. The timeline reflects that at once, but the
	 * right-hand panel kept rendering a form for a document that exists nowhere.
	 *
	 * That is not merely stale: the next keystroke queues a PUT for a path with no
	 * ref, which the batch route's reachability gate refuses — surfacing as a
	 * spurious "changes are not saved" (the DEV-663 symptom) over an edit that
	 * could never have landed. The pending entry goes for the same reason: it is
	 * unsaveable by construction, and the pre-delete snapshot stays recoverable
	 * from the save journal.
	 *
	 * Errs towards staying put. A slug found anywhere in the project counts as
	 * alive, so a false navigation — which would be worse than the staleness this
	 * fixes — needs the slug to be absent everywhere.
	 */
	private leaveSelectionIfItVanished(project: ProjectData): void {
		const nodeType = this.selectedNodeType();
		if (nodeType !== 'scene' && nodeType !== 'image') return;
		const path = this.selectedPath();
		if (!path) return;
		const parsed = this.parseDocPath(path);
		if (!parsed) return;

		const alive = nodeType === 'scene' ? this.allSceneSlugs(project) : this.allImageSlugs(project);
		if (alive.has(parsed.slug)) return;

		this.trackPending(path, false, '');
		this.messageService.add({
			severity: 'info',
			summary: nodeType === 'scene' ? 'Scene removed' : 'Image removed',
			detail: 'It went with the media you deleted — opened its parent instead.',
			life: 5000,
		});

		const chapter = parsed.chapterSlug
			? (project.chapters ?? []).find(c => c.slug === parsed.chapterSlug)
			: undefined;
		if (chapter) this.selectChapter(chapter.slug);
		else this.navigateToProject();
	}

	private syncFormFromDocument(path: string, data: SceneData | ImageData): void {
		if (this.selectedPath() !== path) return;
		const nodeType = this.selectedNodeType();
		if (nodeType !== 'scene' && nodeType !== 'image') return;
		this.formData.set(data as unknown as Record<string, unknown>);
		this.editorContent.set(JSON.stringify(data, null, 2));
	}

	/**
	 * An out-of-form write lands on top of whatever the target already held.
	 * When that target still carries a `pendingChanges` entry — unsaved edits
	 * made before navigating away — the entry holds the *pre-write* JSON, and
	 * the next auto-save tick would PUT it back over the write. Refresh it in
	 * place; the write was built from the same cached document, so it already
	 * contains those edits. Nothing pending means the write's own PUT is the
	 * only one in flight, so leave the map alone.
	 */
	private refreshPendingForPath(path: string, json: string): void {
		if (!this.pendingChanges().has(path)) return;
		this.trackPending(path, true, json);
	}

	/**
	 * The out-of-form write's PUT succeeded, so `json` is now the saved
	 * baseline. Skipped when a *newer* pending entry exists: the user edited the
	 * form while the PUT was in flight and that edit is still genuinely unsaved.
	 */
	private markDocumentSaved(path: string, json: string): void {
		const entry = this.pendingChanges().get(path);
		if (entry) {
			if (entry.json !== json) return;
			// Only touch the map when there is something to drop: replacing it
			// restarts the 600 ms auto-save debounce for every other pending path.
			this.trackPending(path, false, json);
		}
		this.stateService.markClean([path]);
		if (this.selectedPath() === path && this.editorContent() === json) {
			this.loadedContent = json;
			this.editorDirty.set(false);
		}
	}

	/**
	 * The out-of-form write's PUT failed. The user can already see the write in
	 * the form, so leave the path dirty and let the ordinary auto-save retry it
	 * rather than dropping it. A newer pending entry already contains the write
	 * (it was cloned from the mirrored `formData`), so never overwrite that with
	 * this older snapshot.
	 */
	private markDocumentDirty(path: string, json: string): void {
		const entry = this.pendingChanges().get(path);
		if (entry && entry.json !== json) return;
		this.trackPending(path, true, json);
		if (this.selectedPath() === path) this.editorDirty.set(true);
	}

	private scrollFormToTop(): void {
		this.formScroller()?.nativeElement.scrollTo(0, 0);
	}

	private trackPending(path: string | null, dirty: boolean, json: string): void {
		if (!path) return;
		// Only a real file can be saved. A synthetic selection key — today just
		// `chapter:<slug>` — reaching the pending map is a bug at the call site,
		// and one that used to cost far more than the entry itself: the save
		// batch derived its project slug from the first pending path (DEV-657).
		// Refuse it here so no future caller can re-open that hole quietly.
		if (!isProjectFilePath(path)) {
			console.warn('[editor] refusing to queue a path the save endpoint cannot address', { path });
			return;
		}
		this.pendingChanges.update(map => {
			const next = new Map(map);
			if (dirty) {
				try { next.set(path, { json, data: JSON.parse(json) }); } catch { next.set(path, { json, data: undefined }); }
			} else {
				next.delete(path);
			}
			return next;
		});
	}

	/**
	 * Pull paths the service has marked dirty (e.g. by clearPlaybackTypeForMedia)
	 * into pendingChanges so the existing 1.5s auto-save effect picks them up.
	 * Open-form entries already in pendingChanges win — this only fills the gap
	 * for scenes that aren't currently the selected form. saveFile's success
	 * branch calls stateService.markAllClean(), so dirtyPaths and pendingChanges
	 * stay aligned.
	 */
	private flushStateServiceDirtyToPending(): void {
		const dirty = this.stateService.getDirtyEntries();
		if (dirty.length === 0) return;
		this.pendingChanges.update(map => {
			const next = new Map(map);
			for (const entry of dirty) {
				if (!entry.path || next.has(entry.path)) continue;
				const data = entry.data;
				if (data === null || typeof data !== 'object') continue;
				const json = JSON.stringify(data, null, 2);
				next.set(entry.path, { json, data: data as Record<string, unknown> });
			}
			return next;
		});
	}

	private saveConfig(): void {
		this.saveErrorService.setRetry(() => this.saveConfig());
		this.configService.save(this.slug).subscribe({
			error: () => { /* saving flag + SaveErrorService reporting handled by the service */ },
		});
	}
}

function canonicalizeUrlsDeep(node: unknown, prefix: string, canonicalUrl: string): boolean {
	if (node === null || typeof node !== 'object') return false;
	let changed = false;
	if (Array.isArray(node)) {
		for (let i = 0; i < node.length; i++) {
			const v = node[i];
			if (typeof v === 'string' && v !== canonicalUrl && v.startsWith(prefix + '.')) {
				node[i] = canonicalUrl; changed = true;
			} else if (v && typeof v === 'object') {
				if (canonicalizeUrlsDeep(v, prefix, canonicalUrl)) changed = true;
			}
		}
		return changed;
	}
	const obj = node as Record<string, unknown>;
	for (const k of Object.keys(obj)) {
		const v = obj[k];
		if (typeof v === 'string' && v !== canonicalUrl && v.startsWith(prefix + '.')) {
			obj[k] = canonicalUrl; changed = true;
		} else if (v && typeof v === 'object') {
			if (canonicalizeUrlsDeep(v, prefix, canonicalUrl)) changed = true;
		}
	}
	return changed;
}
